import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Maximize2, Copy, Check } from 'lucide-react';
import { apiFetch } from '../api/client';

// ─── Shared types ────────────────────────────────────────────────────────────

interface TWFGroup {
  measured_at: string;
  point_index: number;
  point_name: string;
  axes: Array<{ axis_id: number; axis_num: number; axis_label: string; file_id: number; data_source?: string }>;
}

interface TWFTrace {
  y: number[];
  sample_rate: number;
  duration_sec: number;
  peak_pos: number;
  peak_neg: number;
  rms: number;
  crest_factor: number;
  source: 'spaces' | 'spaces_derived' | 'ei_analytic';
  signal: string;
  unit: string;
  measured_at?: string;
}

interface FFTTrace {
  file_id: number;
  axis_id: number;
  axis_num: number;
  axis_label: string;
  signal: string;
  unit: string;
  sample_rate: number;
  bin_count: number;
  freq_res_hz: number;
  freq_res_cpm: number;
  max_freq_hz: number;
  max_freq_cpm: number;
  amplitudes: number[];
  peak_cpm: number;
  peak_amplitude: number;
  detected_rpm: number | null;
  measured_at: string;
  source: string;
}

export interface WaveformJumpTo {
  measured_at: string;
  point_name: string;
  signal?: string;
}

interface WaveformViewerProps {
  machineId: string;
  jumpTo?: WaveformJumpTo | null;
  defaultPointFilter?: string;
  autoLoad?: boolean;
  operatingRpm?: number | null;
  isVfd?: boolean;
}

// ─── Marker types ────────────────────────────────────────────────────────────

type MarkerType = 'standalone' | 'harmonic' | 'sideband';
interface Marker {
  id: string;
  type: MarkerType;
  cpm: number;
  label: string;
  colour: string;
  // harmonic
  fundamental?: number;
  count?: number;
  // sideband
  centre?: number;
  spacing?: number;
  sbCount?: number;
}

interface PlacementMode {
  markerType: MarkerType;
  step: 'position' | 'spacing' | 'count';
  cursorCpm: number;
  fundamentalCpm?: number;
  spacing: number;
  count: number;
}

interface TwfPlacementMode {
  step: 'position' | 'count';
  cursorSec: number;   // the period candidate in seconds (moves with ← →)
  count: number;       // how many lines to preview
}

interface TwfMarker {
  id: string;
  periodSec: number;
  count: number;
  colour: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const AXIS_COLORS: Record<string, string> = { H: '#2563eb', V: '#16a34a', A: '#06b6d4' };
const AXIS_NUM_COLORS: Record<number, string> = { 1: '#2563eb', 2: '#16a34a', 3: '#06b6d4' };
const SIGNALS = ['velocity', 'accel', 'envelope', 'peakvue'] as const;
type Signal = typeof SIGNALS[number];

function signalLabel(s: Signal) { return s === 'velocity' ? 'Vel' : s === 'accel' ? 'Acc' : s === 'envelope' ? 'Env' : 'Impact'; }
function signalUnit(s: Signal)  { return s === 'velocity' ? 'in/s' : s === 'accel' ? 'G' : s === 'envelope' ? 'GE' : 'G'; }

const PV_SEV_COLOURS: Record<string, string> = {
  normal:      '#16a34a',
  monitor:     '#ca8a04',
  investigate: '#d97706',
  action:      '#ea580c',
  urgent:      '#dc2626',
};

interface PeakVueData {
  max_peak_g:      number;
  rms_g:           number;
  crest_factor:    number;
  severity:        string;
  hp_cutoff_hz:    number;
  // Present only from peakvue-waveform (computation); absent from peakvue-meta (DB-only)
  output_rate_hz?: number;
  n_windows?:      number;
  waveform?:       number[];
  spectrum?:       number[];
  freq_res_hz?:    number;
  // PeakVue Plus
  periodic_pct?:     number;
  autocorr_peak?:    number;
  fault_type?:       string;
  interpretation?:   string;
  plus_reliability?: string;  // 'inactive' | 'preliminary' | 'high'
  peakvue_plus?: {
    periodic_pct:     number;
    autocorr_peak:    number;
    fault_type:       string;
    interpretation:   string;
    plus_reliability: string;
  };
}

const PV_PLUS_FAULT_COLOURS: Record<string, string> = {
  highly_periodic:  '#dc2626',
  bearing_fault:    '#dc2626',  // legacy DB value
  cavitation_noise: '#6b7280',
  lubrication:      '#d97706',
  early_fault:      '#0891b2',
  healthy:          '#16a34a',
};

const PV_PLUS_FAULT_LABELS: Record<string, string> = {
  highly_periodic:  'Highly Periodic',
  bearing_fault:    'Highly Periodic',  // legacy DB value
  cavitation_noise: 'Cavitation/noise',
  lubrication:      'Lubrication',
  early_fault:      'Early fault',
  healthy:          'Healthy',
};

function pvPlusFromData(pv: PeakVueData): { periodic_pct: number; fault_type: string; interpretation: string; plus_reliability: string; autocorr_peak: number } | null {
  const plus = pv.peakvue_plus;
  if (plus) return plus;
  if (pv.periodic_pct != null && pv.fault_type != null && pv.plus_reliability != null) {
    return {
      periodic_pct:     pv.periodic_pct,
      autocorr_peak:    pv.autocorr_peak ?? 0,
      fault_type:       pv.fault_type,
      interpretation:   pv.interpretation ?? '',
      plus_reliability: pv.plus_reliability,
    };
  }
  return null;
}

function periodicBarColour(periodic_pct: number, fault_type: string): string {
  if (periodic_pct < 15) return '#6b7280';
  if (periodic_pct < 30) return '#d97706';
  return PV_PLUS_FAULT_COLOURS[fault_type] ?? '#6b7280';
}

function uid() { return Math.random().toString(36).slice(2); }

function fmtEastern(ts: string): string {
  if (!ts) return '';
  const [datePart, timePart = '00:00:00'] = ts.split(' ');
  const sep = datePart.includes('/') ? '/' : '-';
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = (timePart || '').split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;
  return `${months[m-1]} ${d}, ${y} ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

// ─── TWF decimation ──────────────────────────────────────────────────────────

function decimateTrace(y: number[], pixelWidth: number, iStart: number, iEnd: number): Array<[number, number]> {
  const count = iEnd - iStart;
  const result: Array<[number, number]> = new Array(pixelWidth);
  for (let px = 0; px < pixelWidth; px++) {
    const lo = iStart + Math.floor((px / pixelWidth) * count);
    const hi = iStart + Math.floor(((px + 1) / pixelWidth) * count);
    let mn = Infinity, mx = -Infinity;
    for (let i = lo; i < hi && i < y.length; i++) { if (y[i] < mn) mn = y[i]; if (y[i] > mx) mx = y[i]; }
    result[px] = [mn === Infinity ? 0 : mn, mx === -Infinity ? 0 : mx];
  }
  return result;
}

// ─── Module-level zoom lock (persists across component remounts) ──────────────

interface ZoomLock {
  fftXMin: number; fftXMax: number; fftYMax: number;
  twfZoomRange: [number, number]; twfYMin: number; twfYMax: number;
}
let _waveformZoomLock: ZoomLock | null = null;

// ─── FFT canvas renderer ─────────────────────────────────────────────────────

function renderFFT(
  canvas: HTMLCanvasElement,
  traces: FFTTrace[],
  xMinCpm: number,
  xMaxCpm: number,
  markers: Marker[],
  cursorCpm: number | null,
  operatingRpm: number | null,
  showRevMarkers: boolean,
  placementMode: PlacementMode | null,
  dragSelect: { a: number; b: number } | null,
  lockedYMax?: number,
) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.offsetWidth;
  const H = canvas.offsetHeight;
  if (W === 0 || H === 0) return;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  const pad = { top: 30, right: 16, bottom: 46, left: 58 };
  const cW = W - pad.left - pad.right;
  const cH = H - pad.top - pad.bottom;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  const span = xMaxCpm - xMinCpm;
  const cpmToX = (cpm: number) => pad.left + ((cpm - xMinCpm) / span) * cW;

  // Y max — locked value takes priority; otherwise auto-scale from visible bins
  let yMax: number;
  if (lockedYMax != null && lockedYMax > 0) {
    yMax = lockedYMax;
  } else {
    yMax = 0;
    for (const t of traces) {
      const minBin = Math.max(0, Math.floor(xMinCpm / t.freq_res_cpm));
      const maxBin = Math.min(t.bin_count - 1, Math.ceil(xMaxCpm / t.freq_res_cpm));
      for (let i = minBin; i <= maxBin; i++) if (t.amplitudes[i] > yMax) yMax = t.amplitudes[i];
    }
    yMax = yMax > 0 ? yMax * 1.15 : 1;
  }

  // Grid
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 0.5;
  for (let i = 0; i <= 5; i++) {
    const y = pad.top + cH * (1 - i / 5);
    ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + cW, y); ctx.stroke();
  }

  // Y labels
  ctx.fillStyle = '#6b7280';
  ctx.font = `${10 * Math.min(1, W / 500)}px Inter, sans-serif`;
  ctx.textAlign = 'right';
  for (let i = 0; i <= 5; i++) {
    const val = yMax * i / 5;
    const y = pad.top + cH * (1 - i / 5);
    ctx.fillText(val.toFixed(4), pad.left - 4, y + 3);
  }

  // Y unit rotated
  ctx.save();
  ctx.translate(12, pad.top + cH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.fillStyle = '#374151';
  ctx.font = '11px Inter, sans-serif';
  ctx.fillText(traces[0]?.unit ?? 'in/s', 0, 0);
  ctx.restore();

  // X labels
  ctx.textAlign = 'center';
  ctx.fillStyle = '#6b7280';
  ctx.font = '10px Inter, sans-serif';
  for (let i = 0; i <= 6; i++) {
    const cpm = xMinCpm + span * i / 6;
    const x = pad.left + cW * i / 6;
    ctx.fillText(cpm >= 1000 ? `${(cpm / 1000).toFixed(1)}k` : cpm.toFixed(0), x, pad.top + cH + 13);
  }
  ctx.fillStyle = '#374151';
  ctx.font = '11px Inter, sans-serif';
  ctx.fillText('CPM', pad.left + cW / 2, pad.top + cH + 30);

  // Clip to plot area
  ctx.save();
  ctx.beginPath();
  ctx.rect(pad.left, pad.top, cW, cH);
  ctx.clip();

  // Traces
  for (const t of traces) {
    const colour = AXIS_NUM_COLORS[t.axis_num] || '#94a3b8';
    const minBin = Math.max(0, Math.floor(xMinCpm / t.freq_res_cpm) - 1);
    const maxBin = Math.min(t.bin_count - 1, Math.ceil(xMaxCpm / t.freq_res_cpm) + 1);
    ctx.strokeStyle = colour;
    ctx.lineWidth = 0.9;
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    let first = true;
    for (let i = minBin; i <= maxBin; i++) {
      const x = cpmToX(i * t.freq_res_cpm);
      const y = pad.top + cH - (t.amplitudes[i] / yMax) * cH;
      if (first) { ctx.moveTo(x, y); first = false; } else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Auto RPM harmonic markers
  if (showRevMarkers && operatingRpm && operatingRpm > 0) {
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    for (let h = 1; h <= 10; h++) {
      const hcpm = operatingRpm * h;
      if (hcpm < xMinCpm) continue;
      if (hcpm > xMaxCpm) break;
      const x = cpmToX(hcpm);
      ctx.strokeStyle = '#fca5a5';
      ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + cH); ctx.stroke();
      ctx.fillStyle = '#dc2626';
      ctx.font = '9px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`h${h}`, x, pad.top + 10);
      ctx.beginPath(); ctx.moveTo(x - 5, pad.top + 14); ctx.lineTo(x + 5, pad.top + 14); ctx.lineTo(x, pad.top + 22); ctx.closePath();
      ctx.fill();
    }
    ctx.setLineDash([]);
  }

  // User markers
  const drawMarkerLine = (cpm: number, colour: string, dashed: boolean, label: string, yOffset = 0) => {
    if (cpm < xMinCpm || cpm > xMaxCpm) return;
    const x = cpmToX(cpm);
    ctx.strokeStyle = colour;
    ctx.lineWidth = 1;
    if (dashed) ctx.setLineDash([4, 3]); else ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + cH); ctx.stroke();
    ctx.setLineDash([]);
    if (label) {
      ctx.fillStyle = colour;
      ctx.font = '9px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(label, x, pad.top + 10 + yOffset);
    }
  };

  for (const m of markers) {
    if (m.type === 'standalone') {
      drawMarkerLine(m.cpm, m.colour, false, m.label);
    } else if (m.type === 'harmonic' && m.fundamental && m.count) {
      for (let h = 1; h <= m.count; h++) {
        const hcpm = m.fundamental * h;
        drawMarkerLine(hcpm, m.colour, true, `h${h}`);
        if (hcpm >= xMinCpm && hcpm <= xMaxCpm) {
          const x = cpmToX(hcpm);
          ctx.fillStyle = m.colour;
          ctx.beginPath(); ctx.moveTo(x - 4, pad.top + 13); ctx.lineTo(x + 4, pad.top + 13); ctx.lineTo(x, pad.top + 20); ctx.closePath();
          ctx.fill();
        }
      }
    } else if (m.type === 'sideband' && m.centre && m.spacing && m.sbCount) {
      drawMarkerLine(m.centre, m.colour, false, m.label || `${Math.round(m.centre / 1000)}k`);
      for (let s = 1; s <= m.sbCount; s++) {
        drawMarkerLine(m.centre + m.spacing * s, m.colour, true, `+${s}`);
        drawMarkerLine(m.centre - m.spacing * s, m.colour, true, `-${s}`);
      }
    }
  }

  // Placement mode cursor + preview
  if (placementMode) {
    const typeColor = placementMode.markerType === 'standalone' ? '#7c3aed'
      : placementMode.markerType === 'harmonic' ? '#dc2626' : '#d97706';
    const pcpm = placementMode.step === 'position' ? placementMode.cursorCpm : placementMode.fundamentalCpm!;

    if (placementMode.markerType === 'harmonic' && (placementMode.step === 'count' || placementMode.step === 'position')) {
      const fund = placementMode.step === 'count' ? placementMode.fundamentalCpm! : placementMode.cursorCpm;
      const count = placementMode.step === 'count' ? placementMode.count : placementMode.count;
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = typeColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      for (let h = 2; h <= count; h++) {
        const hcpm = fund * h;
        if (hcpm < xMinCpm) continue;
        if (hcpm > xMaxCpm) break;
        const hx = cpmToX(hcpm);
        ctx.beginPath(); ctx.moveTo(hx, pad.top); ctx.lineTo(hx, pad.top + cH); ctx.stroke();
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }
    if (placementMode.markerType === 'sideband' && (placementMode.step === 'position' || placementMode.step === 'spacing' || placementMode.step === 'count')) {
      const fund = placementMode.step === 'position' ? placementMode.cursorCpm : placementMode.fundamentalCpm!;
      const cnt = placementMode.step === 'count' ? placementMode.count : 3;
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = typeColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      for (let s = 1; s <= cnt; s++) {
        [fund + placementMode.spacing * s, fund - placementMode.spacing * s].forEach(scpm => {
          if (scpm >= xMinCpm && scpm <= xMaxCpm) {
            const sx = cpmToX(scpm);
            ctx.beginPath(); ctx.moveTo(sx, pad.top); ctx.lineTo(sx, pad.top + cH); ctx.stroke();
          }
        });
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }

    if (pcpm >= xMinCpm && pcpm <= xMaxCpm) {
      const px = cpmToX(pcpm);
      ctx.strokeStyle = typeColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(px, pad.top); ctx.lineTo(px, pad.top + cH); ctx.stroke();
      ctx.fillStyle = typeColor;
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(pcpm).toLocaleString()} CPM`, px, pad.top - 6 < 8 ? pad.top + 20 : pad.top - 4);
    }
  }

  // Drag selection rectangle
  if (dragSelect) {
    const x1 = cpmToX(Math.min(dragSelect.a, dragSelect.b));
    const x2 = cpmToX(Math.max(dragSelect.a, dragSelect.b));
    ctx.fillStyle = 'rgba(99,102,241,0.12)';
    ctx.fillRect(x1, pad.top, x2 - x1, cH);
    ctx.strokeStyle = 'rgba(99,102,241,0.55)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.strokeRect(x1, pad.top, x2 - x1, cH);
    // Edge labels
    ctx.fillStyle = 'rgba(99,102,241,0.9)';
    ctx.font = '9px Inter, sans-serif';
    ctx.textAlign = 'left';
    const aLabel = `${Math.round(Math.min(dragSelect.a, dragSelect.b)).toLocaleString()}`;
    const bLabel = `${Math.round(Math.max(dragSelect.a, dragSelect.b)).toLocaleString()}`;
    ctx.fillText(aLabel, x1 + 2, pad.top + cH - 4);
    ctx.textAlign = 'right';
    ctx.fillText(bLabel, x2 - 2, pad.top + cH - 4);
  }

  ctx.restore(); // end clip

  // Read cursor
  if (cursorCpm !== null && cursorCpm >= xMinCpm && cursorCpm <= xMaxCpm) {
    const cx = cpmToX(cursorCpm);
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(cx, pad.top); ctx.lineTo(cx, pad.top + cH); ctx.stroke();
    ctx.setLineDash([]);
  }
}

// ─── Main component ──────────────────────────────────────────────────────────

export function WaveformViewer({ machineId, jumpTo, defaultPointFilter, autoLoad, operatingRpm, isVfd }: WaveformViewerProps) {
  // ── Shared state
  const [activeTab, setActiveTab] = useState<'twf' | 'fft'>('twf');
  const [groups, setGroups] = useState<TWFGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPoint, setSelectedPoint] = useState('');
  const [signal, setSignal] = useState<Signal>('velocity');
  const [activeAxes, setActiveAxes] = useState<Set<string>>(new Set(['H', 'V', 'A']));
  const [showRevMarkers, setShowRevMarkers] = useState(false);
  const [localRpm, setLocalRpm] = useState<number | null>(null);
  const [rpmPickMode, setRpmPickMode] = useState(false);
  const [rpmConfirmPending, setRpmConfirmPending] = useState<{ cpm: number; rpm: number } | null>(null);
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [markerForm, setMarkerForm] = useState<null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Zoom lock state (mirrors module-level _waveformZoomLock for UI reactivity)
  const [zoomLocked, setZoomLocked] = useState(() => _waveformZoomLock !== null);
  const fftLastYMaxRef = useRef<number>(1);
  const twfLastYRef = useRef<{ yMin: number; yMax: number }>({ yMin: -1, yMax: 1 });

  // ── TWF state
  const [twfTraces, setTwfTraces] = useState<Record<string, TWFTrace | null>>({});
  const [twfLoading, setTwfLoading] = useState(false);
  const [twfError, setTwfError] = useState('');
  const [zoomRange, setZoomRange] = useState<[number, number]>(() => _waveformZoomLock?.twfZoomRange ?? [0, 1]);
  const [cursorPct, setCursorPct] = useState<number | null>(null);
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const navCanvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ startX: number; startZoom: [number, number]; handle: 'left' | 'right' | 'mid' } | null>(null);

  // ── TWF drag-select state
  const twfDragRef = useRef<{ startPct: number; startX: number } | null>(null);
  const [twfDragSelect, setTwfDragSelect] = useState<{ a: number; b: number } | null>(null);

  // ── FFT state
  const [fftTraces, setFftTraces] = useState<Record<string, FFTTrace | null>>({});
  const [fftLoading, setFftLoading] = useState<Record<string, boolean>>({});
  const [fftError, setFftError] = useState('');
  const [xMinCpm, setXMinCpm] = useState(() => _waveformZoomLock?.fftXMin ?? 0);
  const [xMaxCpm, setXMaxCpm] = useState(() => _waveformZoomLock?.fftXMax ?? 50000);
  const [fftCursorCpm, setFftCursorCpm] = useState<number | null>(null);
  const [fftMousePos, setFftMousePos] = useState<{ x: number; y: number } | null>(null);
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; cpm: number } | null>(null);
  const [twfContextMenu, setTwfContextMenu] = useState<{ x: number; y: number; clickSec: number } | null>(null);
  const [placementMode, setPlacementMode] = useState<PlacementMode | null>(null);
  const [twfPlacementMode, setTwfPlacementMode] = useState<TwfPlacementMode | null>(null);
  const [twfMarkers, setTwfMarkers] = useState<TwfMarker[]>([]);

  // ── TWF harmonic cursor
  const [twfHarmonicsEnabled, setTwfHarmonicsEnabled] = useState(false);
  const [twfHarmonicCenter, setTwfHarmonicCenter] = useState<number | null>(null);
  const [twfHarmonicInterval, setTwfHarmonicInterval] = useState(0.05);
  const twfHarmonicDragRef = useRef<{
    mode: 'center' | 'sideband';
    startTimeSec: number;
    startCenter: number;
    startInterval: number;
    sidebandN: number;
  } | null>(null);
  const fftDragRef = useRef<{ startCpm: number; startX: number } | null>(null);
  const [fftDragSelect, setFftDragSelect] = useState<{ a: number; b: number } | null>(null);
  const fftCanvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef({ placementMode, activeTab, fftTraces, xMaxCpm, cursorPct, zoomRange, twfTraces, twfPlacementMode } as {
    placementMode: PlacementMode | null;
    activeTab: 'twf' | 'fft';
    fftTraces: Record<string, FFTTrace | null>;
    xMaxCpm: number;
    cursorPct: number | null;
    zoomRange: [number, number];
    twfTraces: Record<string, TWFTrace | null>;
    twfPlacementMode: TwfPlacementMode | null;
    twfHarmonicsEnabled: boolean;
    twfHarmonicCenter: number | null;
    twfHarmonicInterval: number;
  });
  // Cache: key = `${file_id}:${axis_id}:${signal}`
  const fftCache = useRef<Record<string, FFTTrace>>({});
  const twfCache = useRef<Record<string, TWFTrace>>({});

  // PeakVue state — key = `${file_id}:${axis_id}`
  // pvCache: full data with waveform/spectrum (from peakvue-waveform, lazy)
  // pvMetaCache: scalar metadata only (from peakvue-meta, eager — no S3, no compute)
  const pvCache = useRef<Record<string, PeakVueData>>({});
  const pvMetaCache = useRef<Record<string, PeakVueData>>({});
  const [pvDataByAxis, setPvDataByAxis] = useState<Record<string, PeakVueData>>({});
  const [pvLoading, setPvLoading] = useState(false);

  // ── Keep stateRef current every render (stable ref for keyboard handler)
  useEffect(() => {
    stateRef.current = { placementMode, activeTab, fftTraces, xMaxCpm, cursorPct, zoomRange, twfTraces, twfPlacementMode, twfHarmonicsEnabled, twfHarmonicCenter, twfHarmonicInterval };
  });

  // ── Auto-place harmonic center at midpoint of visible range when first enabled
  useEffect(() => {
    if (twfHarmonicsEnabled && twfHarmonicCenter === null) {
      const trace = Object.values(twfTraces).find(t => t != null);
      if (!trace) return;
      const n = trace.y.length, sr = trace.sample_rate;
      const iStart = Math.floor(zoomRange[0] * n), iEnd = Math.ceil(zoomRange[1] * n);
      setTwfHarmonicCenter((iStart / sr + iEnd / sr) / 2);
    }
  }, [twfHarmonicsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Harmonic cursor readout (Fund left / right / interval / CPM)
  const twfHarmonicReadout = (() => {
    if (!twfHarmonicsEnabled || twfHarmonicCenter === null) return null;
    const trace = Object.values(twfTraces).find(t => t != null);
    if (!trace) return null;
    const sr = trace.sample_rate;
    const getAmp = (t: number) => {
      const idx = Math.max(0, Math.min(trace.y.length - 1, Math.round(t * sr)));
      return trace.y[idx];
    };
    const lT = twfHarmonicCenter - twfHarmonicInterval;
    const rT = twfHarmonicCenter + twfHarmonicInterval;
    return {
      lT, rT,
      lAmp: getAmp(lT),
      rAmp: getAmp(rT),
      cpm: Math.round(60 / twfHarmonicInterval),
    };
  })();

  // ── Keyboard handler — placement mode + read cursor arrows
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const s = stateRef.current;

      if (e.key === 'Escape') {
        if (s.placementMode) { setPlacementMode(null); e.preventDefault(); }
        if (s.twfPlacementMode) { setTwfPlacementMode(null); e.preventDefault(); }
        setContextMenu(null);
        return;
      }

      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && s.activeTab === 'fft') {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const trace = Object.values(s.fftTraces).find(t => t != null);
        const binRes = trace ? trace.freq_res_cpm : 60;
        const step = binRes * (e.shiftKey ? 50 : 1);

        if (s.placementMode) {
          const pm = s.placementMode;
          if (pm.step === 'position') {
            setPlacementMode(p => p ? { ...p, cursorCpm: Math.max(0, Math.min(s.xMaxCpm, p.cursorCpm + dir * step)) } : p);
          } else if (pm.step === 'spacing') {
            setPlacementMode(p => p ? { ...p, spacing: Math.max(binRes, p.spacing + dir * step) } : p);
          } else if (pm.step === 'count') {
            setPlacementMode(p => p ? { ...p, count: Math.max(1, Math.min(20, p.count + dir)) } : p);
          }
        } else {
          setFftCursorCpm(prev => Math.max(0, Math.min(s.xMaxCpm, (prev ?? 0) + dir * step)));
        }
        return;
      }

      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && s.activeTab === 'twf' && s.twfPlacementMode) {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const trace = Object.values(s.twfTraces).find(t => t != null);
        const sr = trace?.sample_rate ?? 1;
        const sampleStep = (e.shiftKey ? 50 : 1) / sr;
        if (s.twfPlacementMode.step === 'position') {
          setTwfPlacementMode(p => p ? { ...p, cursorSec: Math.max(0.001, p.cursorSec + dir * sampleStep) } : p);
        } else if (s.twfPlacementMode.step === 'count') {
          setTwfPlacementMode(p => p ? { ...p, count: Math.max(1, Math.min(50, p.count + dir)) } : p);
        }
        return;
      }

      // Harmonic cursor: arrows adjust interval, Ctrl+arrows move center
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && s.activeTab === 'twf' && s.twfHarmonicsEnabled && s.twfHarmonicCenter !== null && !s.twfPlacementMode) {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const traces = Object.values(s.twfTraces).filter(t => t != null) as TWFTrace[];
        if (!traces.length) return;
        const sr = traces[0].sample_rate;
        const step = (e.shiftKey ? 10 : 1) / sr;
        if (e.ctrlKey || e.metaKey) {
          setTwfHarmonicCenter(c => (c ?? 0) + dir * step);
        } else {
          setTwfHarmonicInterval(iv => Math.max(2 / sr, iv + dir * step));
        }
        return;
      }

      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && s.activeTab === 'twf' && !s.placementMode && !s.twfPlacementMode) {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const traces = Object.values(s.twfTraces).filter(t => t != null) as TWFTrace[];
        if (!traces.length) return;
        const visibleSamples = (s.zoomRange[1] - s.zoomRange[0]) * traces[0].y.length;
        const pctStep = (e.shiftKey ? 50 : 1) / visibleSamples;
        setCursorPct(prev => Math.max(0, Math.min(1, (prev ?? 0.5) + dir * pctStep)));
        return;
      }

      if (e.key === 'Enter' && s.placementMode && s.activeTab === 'fft') {
        e.preventDefault();
        const pm = s.placementMode;
        if (pm.step === 'position') {
          if (pm.markerType === 'standalone') {
            setMarkers(prev => [...prev, { id: uid(), type: 'standalone', cpm: pm.cursorCpm, label: Math.round(pm.cursorCpm).toLocaleString(), colour: '#7c3aed' }]);
            setPlacementMode(null);
          } else if (pm.markerType === 'harmonic') {
            setPlacementMode(p => p ? { ...p, fundamentalCpm: p.cursorCpm, step: 'count' } : p);
          } else if (pm.markerType === 'sideband') {
            setPlacementMode(p => p ? { ...p, fundamentalCpm: p.cursorCpm, step: 'spacing' } : p);
          }
        } else if (pm.step === 'spacing') {
          setPlacementMode(p => p ? { ...p, step: 'count' } : p);
        } else if (pm.step === 'count') {
          const fund = pm.fundamentalCpm!;
          if (pm.markerType === 'harmonic') {
            setMarkers(prev => [...prev, { id: uid(), type: 'harmonic', cpm: fund, fundamental: fund, count: pm.count, label: `${Math.round(fund / 1000)}k`, colour: '#dc2626' }]);
          } else {
            setMarkers(prev => [...prev, { id: uid(), type: 'sideband', cpm: fund, centre: fund, spacing: pm.spacing, sbCount: pm.count, label: 'SB', colour: '#d97706' }]);
          }
          setPlacementMode(null);
        }
      }

      if (e.key === 'Enter' && s.twfPlacementMode && s.activeTab === 'twf') {
        e.preventDefault();
        const pm = s.twfPlacementMode;
        if (pm.step === 'position') {
          setTwfPlacementMode(p => p ? { ...p, step: 'count' } : p);
        } else if (pm.step === 'count') {
          setTwfMarkers(prev => [...prev, { id: String(Date.now()), periodSec: pm.cursorSec, count: pm.count, colour: '#dc2626' }]);
          setTwfPlacementMode(null);
        }
        return;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Load available groups
  useEffect(() => {
    setGroupsLoading(true);
    apiFetch<TWFGroup[]>(`/measurements/twf-available?machine_id=${machineId}&limit=500`)
      .then(data => {
        setGroups(data || []);
        if (data && data.length > 0 && !jumpTo) {
          setSelectedDate(data[0].measured_at);
          setSelectedPoint(data[0].point_name);
        }
      })
      .catch(() => setGroups([]))
      .finally(() => setGroupsLoading(false));
  }, [machineId]);

  // ── jumpTo effect — re-fetch groups anchored to the target date if not already loaded
  useEffect(() => {
    if (!jumpTo) return;
    const targetDate = jumpTo.measured_at;
    const alreadyLoaded = groups.some(g => g.measured_at === targetDate);
    const doJump = () => {
      setSelectedDate(targetDate);
      setSelectedPoint(jumpTo.point_name);
      if (jumpTo.signal && (SIGNALS as readonly string[]).includes(jumpTo.signal)) {
        setSignal(jumpTo.signal as Signal);
      }
      if (!autoLoad) {
        setTimeout(() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    };
    if (alreadyLoaded) {
      doJump();
    } else {
      // Target date not in loaded window — re-fetch anchored at that date
      setGroupsLoading(true);
      apiFetch<TWFGroup[]>(
        `/measurements/twf-available?machine_id=${machineId}&limit=500&before=${encodeURIComponent(targetDate)}`
      )
        .then(data => {
          setGroups(data || []);
          doJump();
        })
        .catch(() => doJump()) // fall through; viewer will show "not available"
        .finally(() => setGroupsLoading(false));
    }
  }, [jumpTo]);

  // ── Derived selections
  const [pointFilter, setPointFilter] = useState(defaultPointFilter ?? ''); // '' = all points

  // Apply external defaultPointFilter when it changes (e.g. hierarchy click)
  useEffect(() => {
    setPointFilter(defaultPointFilter ?? '');
  }, [defaultPointFilter]);

  const uniquePoints = useMemo(() => {
    const seen = new Set<string>();
    return groups.filter(g => seen.has(g.point_name) ? false : (seen.add(g.point_name), true)).map(g => g.point_name);
  }, [groups]);

  const navGroups = useMemo(() => {
    if (!pointFilter) return groups;
    return groups.filter(g => g.point_name === pointFilter);
  }, [groups, pointFilter]);

  // When filter changes, jump to first entry in filtered list
  useEffect(() => {
    if (navGroups.length > 0) {
      setSelectedDate(navGroups[0].measured_at);
      setSelectedPoint(navGroups[0].point_name);
    }
  }, [pointFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const dates = useMemo(() => {
    const seen = new Set<string>();
    return navGroups.filter(g => seen.has(g.measured_at) ? false : (seen.add(g.measured_at), true)).map(g => g.measured_at);
  }, [navGroups]);

  const pointsForDate = useMemo(() => navGroups.filter(g => g.measured_at === selectedDate), [navGroups, selectedDate]);
  const currentGroup = useMemo(() => pointsForDate.find(g => g.point_name === selectedPoint) || pointsForDate[0] || null, [pointsForDate, selectedPoint]);
  const currentGroupIdx = useMemo(() => navGroups.findIndex(g => g.measured_at === selectedDate && g.point_name === (currentGroup?.point_name ?? '')), [navGroups, selectedDate, currentGroup]);

  const goPrev = () => { if (currentGroupIdx > 0) { const g = navGroups[currentGroupIdx - 1]; setSelectedDate(g.measured_at); setSelectedPoint(g.point_name); } };
  const goNext = () => { if (currentGroupIdx < navGroups.length - 1) { const g = navGroups[currentGroupIdx + 1]; setSelectedDate(g.measured_at); setSelectedPoint(g.point_name); } };

  // ── TWF fetch
  useEffect(() => {
    if (!currentGroup || activeTab !== 'twf' || signal === 'peakvue') return;
    setTwfLoading(true);
    setTwfError('');
    setZoomRange([0, 1]);
    Promise.all(
      currentGroup.axes.map(ax => {
        const cacheKey = `${ax.file_id}:${ax.axis_id}:${signal}`;
        if (twfCache.current[cacheKey]) return Promise.resolve([ax.axis_label, twfCache.current[cacheKey]] as [string, TWFTrace]);
        return apiFetch<TWFTrace>(`/measurements/twf?file_id=${ax.file_id}&axis_id=${ax.axis_id}&signal=${signal}`)
          .then(data => { twfCache.current[cacheKey] = data; return [ax.axis_label, data] as [string, TWFTrace]; })
          .catch(() => [ax.axis_label, null] as [string, null]);
      })
    ).then(results => {
      const t: Record<string, TWFTrace | null> = {};
      for (const [label, data] of results) t[label] = data;
      setTwfTraces(t);
    }).catch(err => setTwfError(String(err))).finally(() => setTwfLoading(false));
  }, [currentGroup, signal, activeTab]);

  // ── FFT fetch
  useEffect(() => {
    if (!currentGroup || activeTab !== 'fft' || signal === 'peakvue') return;
    setFftError('');
    const newLoading: Record<string, boolean> = {};
    const newTraces: Record<string, FFTTrace | null> = {};

    currentGroup.axes.forEach(ax => {
      const cacheKey = `${ax.file_id}:${ax.axis_id}:${signal}`;
      if (fftCache.current[cacheKey]) {
        newTraces[ax.axis_label] = fftCache.current[cacheKey];
      } else {
        newLoading[ax.axis_label] = true;
      }
    });

    setFftTraces(newTraces);
    setFftLoading(newLoading);

    currentGroup.axes.forEach(ax => {
      const cacheKey = `${ax.file_id}:${ax.axis_id}:${signal}`;
      if (fftCache.current[cacheKey]) return;
      apiFetch<FFTTrace>(`/measurements/fft?file_id=${ax.file_id}&axis_id=${ax.axis_id}&signal=${signal}`)
        .then(data => {
          fftCache.current[cacheKey] = data;
          setFftTraces(prev => ({ ...prev, [ax.axis_label]: data }));
        })
        .catch(() => setFftTraces(prev => ({ ...prev, [ax.axis_label]: null })))
        .finally(() => setFftLoading(prev => { const n = { ...prev }; delete n[ax.axis_label]; return n; }));
    });
  }, [currentGroup, signal, activeTab]);

  // ── PeakVue metadata — fast DB-only fetch on every group change (info panel always visible)
  // Uses peakvue-meta (~10ms, no S3, no compute) instead of peakvue-waveform (~100ms+).
  // pvCache (full) takes priority if already populated from a previous PeakVue tab visit.
  useEffect(() => {
    if (!currentGroup) return;

    const result: Record<string, PeakVueData> = {};
    const toFetch: typeof currentGroup.axes = [];
    for (const ax of currentGroup.axes) {
      const key = `${ax.file_id}:${ax.axis_id}`;
      if (pvCache.current[key])     result[ax.axis_label] = pvCache.current[key];
      else if (pvMetaCache.current[key]) result[ax.axis_label] = pvMetaCache.current[key];
      else toFetch.push(ax);
    }

    if (toFetch.length === 0) { setPvDataByAxis(result); return; }

    setPvLoading(true);
    Promise.all(
      toFetch.map(ax => {
        const key = `${ax.file_id}:${ax.axis_id}`;
        return apiFetch<PeakVueData>(`/measurements/peakvue-meta?file_id=${ax.file_id}&axis_id=${ax.axis_id}`)
          .then(d => { pvMetaCache.current[key] = d; return [ax.axis_label, d] as [string, PeakVueData]; })
          .catch(() => [ax.axis_label, null] as [string, null]);
      })
    ).then(fetched => {
      for (const [label, d] of fetched) if (d) result[label] = d;
      setPvDataByAxis(result);
    }).finally(() => setPvLoading(false));
  }, [currentGroup]);

  // ── PeakVue waveform — lazy, only fetched when PeakVue signal tab is selected.
  // By this point the TWF for the group was already prefetched, so the S3 object
  // is in the LRU cache — compute_peakvue runs without an S3 round-trip (~50ms).
  useEffect(() => {
    if (signal !== 'peakvue' || !currentGroup) return;
    const needsFetch = currentGroup.axes.filter(ax => !pvCache.current[`${ax.file_id}:${ax.axis_id}`]?.waveform?.length);
    if (needsFetch.length === 0) return;

    setPvLoading(true);
    Promise.all(
      needsFetch.map(ax => {
        const key = `${ax.file_id}:${ax.axis_id}`;
        return apiFetch<PeakVueData>(`/measurements/peakvue-waveform?file_id=${ax.file_id}&axis_id=${ax.axis_id}`)
          .then(d => { pvCache.current[key] = d; return [ax.axis_label, d] as [string, PeakVueData]; })
          .catch(() => [ax.axis_label, null] as [string, null]);
      })
    ).then(fetched => {
      setPvDataByAxis(prev => {
        const updated = { ...prev };
        for (const [label, d] of fetched) if (d) updated[label] = d;
        return updated;
      });
    }).finally(() => setPvLoading(false));
  }, [signal, currentGroup]);

  // ── Prefetch adjacent groups (N-1, N+1, N+2) and cross-prefetch secondary signal.
  // peakvue-waveform is NOT prefetched here — the TWF prefetch warms the S3 LRU cache,
  // so when the analyst clicks PeakVue the compute path runs without an S3 round-trip.
  useEffect(() => {
    if (signal === 'peakvue') return;
    // Cross-prefetch: if on velocity also warm accel, and vice versa
    const altSignal: Signal | null = signal === 'velocity' ? 'accel' : signal === 'accel' ? 'velocity' : null;
    const sigsToFetch = altSignal ? [signal, altSignal] : [signal];

    const prefetchGroup = (group: TWFGroup | null | undefined) => {
      if (!group) return;
      group.axes.forEach(ax => {
        // Warm PeakVue metadata (fast) for adjacent groups
        const metaKey = `${ax.file_id}:${ax.axis_id}`;
        if (!pvMetaCache.current[metaKey] && !pvCache.current[metaKey]) {
          apiFetch<PeakVueData>(`/measurements/peakvue-meta?file_id=${ax.file_id}&axis_id=${ax.axis_id}`)
            .then(d => { pvMetaCache.current[metaKey] = d; })
            .catch(() => {});
        }
        for (const sig of sigsToFetch) {
          const twfKey = `${ax.file_id}:${ax.axis_id}:${sig}`;
          if (!twfCache.current[twfKey]) {
            apiFetch<TWFTrace>(`/measurements/twf?file_id=${ax.file_id}&axis_id=${ax.axis_id}&signal=${sig}`)
              .then(d => { twfCache.current[twfKey] = d; })
              .catch(() => {});
          }
          const fftKey = `${ax.file_id}:${ax.axis_id}:${sig}`;
          if (!fftCache.current[fftKey]) {
            apiFetch<FFTTrace>(`/measurements/fft?file_id=${ax.file_id}&axis_id=${ax.axis_id}&signal=${sig}`)
              .then(d => { fftCache.current[fftKey] = d; })
              .catch(() => {});
          }
        }
      });
    };

    // Fire-and-forget — errors silently swallowed, never block current view
    prefetchGroup(navGroups[currentGroupIdx - 1]);  // backward nav
    prefetchGroup(navGroups[currentGroupIdx + 1]);  // next
    prefetchGroup(navGroups[currentGroupIdx + 2]);  // two ahead (fast double-tap)
  }, [currentGroupIdx, navGroups, signal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When signal='peakvue', populate twfTraces/fftTraces from pvDataByAxis
  useEffect(() => {
    if (signal !== 'peakvue' || !currentGroup) return;
    const t: Record<string, TWFTrace | null> = {};
    const f: Record<string, FFTTrace | null> = {};
    for (const ax of currentGroup.axes) {
      const pv = pvDataByAxis[ax.axis_label];
      if (!pv || !pv.waveform?.length) { t[ax.axis_label] = null; f[ax.axis_label] = null; continue; }
      t[ax.axis_label] = {
        y:            pv.waveform,
        sample_rate:  pv.output_rate_hz,
        duration_sec: pv.waveform.length / pv.output_rate_hz,
        peak_pos:     pv.max_peak_g,
        peak_neg:     0,
        rms:          pv.rms_g,
        crest_factor: pv.crest_factor,
        source:       'spaces',
        signal:       'peakvue',
        unit:         'G',
      };
      const n = pv.spectrum.length;
      f[ax.axis_label] = {
        file_id:       ax.file_id,
        axis_id:       ax.axis_id,
        axis_num:      ax.axis_num,
        axis_label:    ax.axis_label,
        signal:        'peakvue',
        unit:          'G',
        sample_rate:   pv.output_rate_hz,
        bin_count:     n,
        freq_res_hz:   pv.freq_res_hz,
        freq_res_cpm:  pv.freq_res_hz * 60,
        max_freq_hz:   (n - 1) * pv.freq_res_hz,
        max_freq_cpm:  (n - 1) * pv.freq_res_hz * 60,
        amplitudes:    pv.spectrum,
        peak_cpm:      0,
        peak_amplitude: Math.max(...pv.spectrum),
        detected_rpm:  null,
        measured_at:   '',
        source:        'spaces',
      };
    }
    setTwfTraces(t);
    setFftTraces(f);
    setZoomRange([0, 1]);
  }, [signal, pvDataByAxis, currentGroup]);

  // ── Force xMaxCpm to 30,000 CPM when PeakVue is selected (stress wave domain = 0–500 Hz)
  useEffect(() => {
    if (signal === 'peakvue') { setXMaxCpm(30000); setXMinCpm(0); }
  }, [signal]);

  // ── Effective RPM — local override takes precedence over prop
  const effectiveRpm = localRpm ?? operatingRpm ?? null;

  // ── Toggle axis
  const toggleAxis = (label: string) => {
    setActiveAxes(prev => {
      const next = new Set(prev);
      if (next.has(label)) { if (next.size > 1) next.delete(label); } else next.add(label);
      return next;
    });
  };

  // ── TWF canvas draw
  const drawTWFMain = useCallback(() => {
    const canvas = mainCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth, H = canvas.offsetHeight;
    if (!W || !H) return;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    const activeTraces = (Object.entries(twfTraces).filter(([l, t]) => t && t.y.length > 0 && activeAxes.has(l)) as [string, TWFTrace][]);
    if (!activeTraces.length) {
      ctx.fillStyle = '#9ca3af'; ctx.font = '13px Inter,sans-serif'; ctx.textAlign = 'center';
      const noDataMsg = twfLoading ? 'Loading…'
        : (signal === 'velocity' || signal === 'envelope')
          ? `${signalLabel(signal)} waveform not available for this file`
          : 'No waveform data';
      ctx.fillText(noDataMsg, W / 2, H / 2);
      return;
    }

    const PAD = { top: 8, bottom: 20, left: 48, right: 8 };
    const cW = W - PAD.left - PAD.right, cH = H - PAD.top - PAD.bottom;

    // DC removal: subtract per-axis mean so all axes overlap around zero.
    // Skipped for PeakVue (single-sided signal — mean is background level, not DC offset).
    const dcRemove = signal !== 'peakvue';
    const traceMeans: Record<string, number> = {};
    if (dcRemove) {
      for (const [label, trace] of activeTraces)
        traceMeans[label] = trace.y.reduce((s, v) => s + v, 0) / trace.y.length;
    }

    let yMin = Infinity, yMax = -Infinity;
    for (const [label, t] of activeTraces) {
      const mean = traceMeans[label] ?? 0;
      for (const v of t.y) { const cv = v - mean; if (cv < yMin) yMin = cv; if (cv > yMax) yMax = cv; }
    }
    const yPad = (yMax - yMin || 1) * 0.08; yMin -= yPad; yMax += yPad;
    twfLastYRef.current = { yMin, yMax };
    if (_waveformZoomLock?.twfYMin != null) yMin = _waveformZoomLock.twfYMin;
    if (_waveformZoomLock?.twfYMax != null) yMax = _waveformZoomLock.twfYMax;
    const n = activeTraces[0][1].y.length, sr = activeTraces[0][1].sample_rate;
    const iStart = Math.floor(zoomRange[0] * n), iEnd = Math.ceil(zoomRange[1] * n);

    ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 0.5;
    for (let i = 0; i <= 5; i++) {
      const y = PAD.top + (i / 5) * cH;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + cW, y); ctx.stroke();
      ctx.fillStyle = '#6b7280'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
      ctx.fillText((yMax - (i / 5) * (yMax - yMin)).toFixed(3), PAD.left - 3, y + 3);
    }

    ctx.fillStyle = '#6b7280'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
    const tStart = iStart / sr, tEnd = iEnd / sr;
    for (let i = 0; i <= 4; i++) {
      const x = PAD.left + (i / 4) * cW;
      ctx.fillText((tStart + (i / 4) * (tEnd - tStart)).toFixed(3) + 's', x, H - 4);
    }

    if (yMin < 0 && yMax > 0) {
      const zeroY = PAD.top + (yMax / (yMax - yMin)) * cH;
      ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(PAD.left, zeroY); ctx.lineTo(PAD.left + cW, zeroY); ctx.stroke();
      ctx.setLineDash([]);
    }

    // PeakVue severity reference lines
    if (signal === 'peakvue') {
      const pvLines = [
        { g: 5,  label: 'Monitor',     colour: '#ca8a04' },
        { g: 10, label: 'Investigate', colour: '#d97706' },
        { g: 20, label: 'Action',      colour: '#ea580c' },
        { g: 40, label: 'Urgent',      colour: '#dc2626' },
      ];
      for (const { g, label, colour } of pvLines) {
        if (g > yMax || g < yMin) continue;
        const ry = PAD.top + ((yMax - g) / (yMax - yMin)) * cH;
        ctx.strokeStyle = colour; ctx.lineWidth = 1; ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.moveTo(PAD.left, ry); ctx.lineTo(PAD.left + cW, ry); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = colour; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText(label, PAD.left + cW - 3, ry - 2);
      }
    }

    // Rev markers
    if (showRevMarkers && effectiveRpm && effectiveRpm > 0) {
      const revPeriodSec = 60 / effectiveRpm;
      const t0 = iStart / sr, t1 = iEnd / sr, dur = t1 - t0;
      const firstRevT = Math.ceil(t0 / revPeriodSec) * revPeriodSec;
      ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 1; ctx.globalAlpha = 0.6; ctx.setLineDash([3, 4]);
      for (let t = firstRevT; t <= t1; t += revPeriodSec) {
        const xf = PAD.left + ((t - t0) / dur) * cW;
        ctx.beginPath(); ctx.moveTo(xf, PAD.top); ctx.lineTo(xf, PAD.top + cH); ctx.stroke();
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }

    // TWF harmonic cursor
    if (twfHarmonicsEnabled && twfHarmonicCenter !== null) {
      const t0 = iStart / sr, t1 = iEnd / sr, dur = t1 - t0;
      const ctr = twfHarmonicCenter, ivl = twfHarmonicInterval;
      const HC = 5;

      // Hatch fill between -1 and +1 fundamentals
      const xL = PAD.left + ((ctr - ivl - t0) / dur) * cW;
      const xR = PAD.left + ((ctr + ivl - t0) / dur) * cW;
      const x1c = Math.max(PAD.left, xL), x2c = Math.min(PAD.left + cW, xR);
      if (x2c > x1c) {
        ctx.save();
        ctx.beginPath(); ctx.rect(x1c, PAD.top, x2c - x1c, cH); ctx.clip();
        ctx.fillStyle = 'rgba(220,38,38,0.06)'; ctx.fillRect(x1c, PAD.top, x2c - x1c, cH);
        ctx.strokeStyle = 'rgba(220,38,38,0.12)'; ctx.lineWidth = 1; ctx.setLineDash([]);
        for (let xi = x1c - cH; xi < x2c + cH; xi += 9) {
          ctx.beginPath(); ctx.moveTo(xi, PAD.top); ctx.lineTo(xi + cH, PAD.top + cH); ctx.stroke();
        }
        ctx.restore();
      }

      // Cursor lines
      ctx.font = 'bold 9px Inter,sans-serif'; ctx.textAlign = 'center';
      for (let n = -HC; n <= HC; n++) {
        const t = ctr + n * ivl;
        if (t < t0 || t > t1) continue;
        const xPos = PAD.left + ((t - t0) / dur) * cW;
        const isCenter = n === 0;
        ctx.strokeStyle = '#dc2626';
        ctx.lineWidth = isCenter ? 2 : 1;
        ctx.globalAlpha = isCenter ? 0.9 : 0.65;
        ctx.setLineDash(isCenter ? [] : [4, 3]);
        ctx.beginPath(); ctx.moveTo(xPos, PAD.top); ctx.lineTo(xPos, PAD.top + cH); ctx.stroke();
        if (!isCenter) {
          ctx.fillStyle = '#dc2626'; ctx.globalAlpha = 0.75;
          ctx.fillText(String(n), xPos, PAD.top + cH - 3);
        }
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }

    for (const [label, trace] of activeTraces) {
      const color = AXIS_COLORS[label] || '#94a3b8';
      const mean = traceMeans[label] ?? 0;
      const dec = decimateTrace(trace.y, cW, iStart, iEnd);
      ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.beginPath();
      for (let px = 0; px < dec.length; px++) {
        const [mn, mx] = dec[px], x = PAD.left + px;
        const y1 = PAD.top + ((yMax - (mx - mean)) / (yMax - yMin)) * cH;
        const y2 = PAD.top + ((yMax - (mn - mean)) / (yMax - yMin)) * cH;
        if (px === 0) ctx.moveTo(x, (y1 + y2) / 2);
        ctx.lineTo(x, y1); ctx.lineTo(x, y2);
      }
      ctx.stroke();
    }

    if (cursorPct !== null) {
      const cx = PAD.left + cursorPct * cW;
      ctx.strokeStyle = 'rgba(55,65,81,0.4)'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(cx, PAD.top); ctx.lineTo(cx, PAD.top + cH); ctx.stroke();
      const cursorIdx = iStart + Math.floor(cursorPct * (iEnd - iStart));
      const readouts = [`t = ${(cursorIdx / sr).toFixed(4)}s`];
      for (const [l, t] of activeTraces) readouts.push(`${l}: ${(t.y[Math.min(cursorIdx, t.y.length - 1)] - (traceMeans[l] ?? 0)).toFixed(4)}`);
      const boxW = 92, lineH = 14;
      const rx = Math.min(cx + 6, PAD.left + cW - boxW - 2);
      ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.fillRect(rx, PAD.top + 4, boxW, lineH * readouts.length + 8);
      ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 0.5; ctx.strokeRect(rx, PAD.top + 4, boxW, lineH * readouts.length + 8);
      ctx.fillStyle = '#374151'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'left';
      readouts.forEach((r, i) => ctx.fillText(r, rx + 4, PAD.top + 13 + i * lineH));
    }

    // Drag selection
    if (twfDragSelect) {
      const a = Math.min(twfDragSelect.a, twfDragSelect.b);
      const b = Math.max(twfDragSelect.a, twfDragSelect.b);
      const x1 = PAD.left + a * cW, x2 = PAD.left + b * cW;
      ctx.fillStyle = 'rgba(99,102,241,0.12)';
      ctx.fillRect(x1, PAD.top, x2 - x1, cH);
      ctx.strokeStyle = 'rgba(99,102,241,0.55)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      ctx.strokeRect(x1, PAD.top, x2 - x1, cH);
      // Time labels at edges
      const t1 = (iStart + a * (iEnd - iStart)) / sr;
      const t2 = (iStart + b * (iEnd - iStart)) / sr;
      ctx.fillStyle = 'rgba(99,102,241,0.9)';
      ctx.font = '9px Inter,sans-serif';
      ctx.textAlign = 'left'; ctx.fillText(t1.toFixed(3) + 's', x1 + 2, PAD.top + cH - 4);
      ctx.textAlign = 'right'; ctx.fillText(t2.toFixed(3) + 's', x2 - 2, PAD.top + cH - 4);
    }

    // TWF placement mode preview lines
    if (twfPlacementMode) {
      const T = twfPlacementMode.cursorSec;
      const t0 = iStart / sr, t1 = iEnd / sr, dur = t1 - t0;
      const count = twfPlacementMode.count;
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.7;
      ctx.setLineDash([]);
      // 1× line (solid)
      const x1 = PAD.left + ((T - t0) / dur) * cW;
      if (T >= t0 && T <= t1) {
        ctx.beginPath(); ctx.moveTo(x1, PAD.top); ctx.lineTo(x1, PAD.top + cH); ctx.stroke();
        ctx.fillStyle = '#dc2626'; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('1×', x1 + 2, PAD.top + 12);
      }
      // 2× and beyond (dashed)
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1;
      for (let h = 2; h <= count; h++) {
        const hT = T * h;
        if (hT > t1) break;
        if (hT < t0) continue;
        const hx = PAD.left + ((hT - t0) / dur) * cW;
        ctx.beginPath(); ctx.moveTo(hx, PAD.top); ctx.lineTo(hx, PAD.top + cH); ctx.stroke();
        if (hT - t0 > 0.02 * dur) { // only label if not too close to left edge
          ctx.fillStyle = '#dc2626'; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'left';
          ctx.fillText(`${h}×`, hx + 2, PAD.top + 12);
        }
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }

    // Saved TWF markers
    for (const m of twfMarkers) {
      const T = m.periodSec;
      const t0 = iStart / sr, t1 = iEnd / sr, dur = t1 - t0;
      ctx.strokeStyle = m.colour;
      ctx.globalAlpha = 0.55;
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1;
      for (let h = 1; h <= m.count; h++) {
        const hT = T * h;
        if (hT > t1) break;
        if (hT < t0) continue;
        const hx = PAD.left + ((hT - t0) / dur) * cW;
        ctx.beginPath(); ctx.moveTo(hx, PAD.top); ctx.lineTo(hx, PAD.top + cH); ctx.stroke();
        ctx.fillStyle = m.colour; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(`${h}×`, hx + 2, PAD.top + 12);
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }
  }, [twfTraces, zoomRange, cursorPct, activeAxes, twfLoading, showRevMarkers, effectiveRpm, twfDragSelect, signal, twfPlacementMode, twfMarkers, twfHarmonicsEnabled, twfHarmonicCenter, twfHarmonicInterval]);

  const drawTWFNav = useCallback(() => {
    const canvas = navCanvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth, H = canvas.offsetHeight;
    if (!W || !H) return;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#f9fafb'; ctx.fillRect(0, 0, W, H);
    const activeTraces = (Object.entries(twfTraces).filter(([l, t]) => t && t.y.length > 0 && activeAxes.has(l)) as [string, TWFTrace][]);
    if (!activeTraces.length) return;
    const navDcRemove = signal !== 'peakvue';
    const navMeans: Record<string, number> = {};
    if (navDcRemove) {
      for (const [label, trace] of activeTraces)
        navMeans[label] = trace.y.reduce((s, v) => s + v, 0) / trace.y.length;
    }
    let yMin = Infinity, yMax = -Infinity;
    for (const [label, t] of activeTraces) {
      const mean = navMeans[label] ?? 0;
      for (const v of t.y) { const cv = v - mean; if (cv < yMin) yMin = cv; if (cv > yMax) yMax = cv; }
    }
    const yr = yMax - yMin || 1; yMin -= yr * 0.05; yMax += yr * 0.05;
    const n = activeTraces[0][1].y.length;
    for (const [label, trace] of activeTraces) {
      const color = AXIS_COLORS[label] || '#94a3b8';
      const mean = navMeans[label] ?? 0;
      const dec = decimateTrace(trace.y, W, 0, n);
      ctx.strokeStyle = color; ctx.globalAlpha = 0.7; ctx.lineWidth = 1; ctx.beginPath();
      for (let px = 0; px < dec.length; px++) {
        const [mn, mx] = dec[px];
        const y1 = H - ((mx - mean - yMin) / (yMax - yMin)) * H, y2 = H - ((mn - mean - yMin) / (yMax - yMin)) * H;
        if (px === 0) ctx.moveTo(px, (y1 + y2) / 2); ctx.lineTo(px, y1); ctx.lineTo(px, y2);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const z0 = zoomRange[0] * W, z1 = zoomRange[1] * W;
    ctx.fillStyle = 'rgba(209,213,219,0.55)'; ctx.fillRect(0, 0, z0, H); ctx.fillRect(z1, 0, W - z1, H);
    ctx.strokeStyle = 'rgba(107,114,128,0.6)'; ctx.lineWidth = 1; ctx.strokeRect(z0, 0, z1 - z0, H);
    ctx.fillStyle = 'rgba(107,114,128,0.8)'; ctx.fillRect(z0 - 2, 0, 4, H); ctx.fillRect(z1 - 2, 0, 4, H);
  }, [twfTraces, zoomRange, activeAxes]);

  useEffect(() => { if (activeTab === 'twf') { drawTWFMain(); drawTWFNav(); } }, [drawTWFMain, drawTWFNav, activeTab]);

  // ── FFT canvas draw
  const drawFFT = useCallback(() => {
    const canvas = fftCanvasRef.current;
    if (!canvas) return;
    const visibleTraces = Object.entries(fftTraces)
      .filter(([l, t]) => t && activeAxes.has(l))
      .map(([, t]) => t as FFTTrace);
    if (!visibleTraces.length) {
      const dpr = window.devicePixelRatio || 1;
      const W = canvas.offsetWidth, H = canvas.offsetHeight;
      if (!W || !H) return;
      canvas.width = W * dpr; canvas.height = H * dpr;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#9ca3af'; ctx.font = '13px Inter,sans-serif'; ctx.textAlign = 'center';
      const anyLoading = Object.keys(fftLoading).length > 0;
      const noFftMsg = anyLoading ? 'Loading FFT…'
        : (signal === 'velocity' || signal === 'envelope')
          ? `${signalLabel(signal)} FFT not available for this file`
          : 'No FFT data';
      ctx.fillText(noFftMsg, W / 2, H / 2);
      return;
    }
    // Track last computed yMax (for zoom lock capture)
    let computedYMax = 0;
    for (const t of visibleTraces) {
      const minBin = Math.max(0, Math.floor(xMinCpm / t.freq_res_cpm));
      const maxBin = Math.min(t.bin_count - 1, Math.ceil(xMaxCpm / t.freq_res_cpm));
      for (let i = minBin; i <= maxBin; i++) if (t.amplitudes[i] > computedYMax) computedYMax = t.amplitudes[i];
    }
    fftLastYMaxRef.current = computedYMax > 0 ? computedYMax * 1.15 : 1;
    renderFFT(canvas, visibleTraces, xMinCpm, xMaxCpm, markers, fftCursorCpm, effectiveRpm, showRevMarkers, placementMode, fftDragSelect, _waveformZoomLock?.fftYMax);
  }, [fftTraces, activeAxes, xMinCpm, xMaxCpm, markers, fftCursorCpm, effectiveRpm, showRevMarkers, fftLoading, placementMode, fftDragSelect, zoomLocked]);

  useEffect(() => { if (activeTab === 'fft') drawFFT(); }, [drawFFT, activeTab]);

  // Redraw TWF when switching back
  useEffect(() => {
    if (activeTab === 'twf') { setTimeout(() => { drawTWFMain(); drawTWFNav(); }, 10); }
    if (activeTab === 'fft') { setTimeout(() => drawFFT(), 10); }
  }, [activeTab]);

  // ── Nav drag handlers (TWF)
  const onNavMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const [z0, z1] = zoomRange;
    let handle: 'left' | 'right' | 'mid' | null = null;
    if (Math.abs(pct - z0) < 0.02) handle = 'left';
    else if (Math.abs(pct - z1) < 0.02) handle = 'right';
    else if (pct > z0 && pct < z1) handle = 'mid';
    if (!handle) {
      const span = z1 - z0, half = span / 2;
      setZoomRange([Math.max(0, Math.min(1 - span, pct - half)), Math.max(0, Math.min(1 - span, pct - half)) + span]);
      return;
    }
    dragRef.current = { startX: pct, startZoom: [z0, z1], handle };
  };
  const onNavMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!dragRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const delta = pct - dragRef.current.startX;
    const [z0, z1] = dragRef.current.startZoom;
    const MIN = 0.02;
    if (dragRef.current.handle === 'left') setZoomRange([Math.max(0, Math.min(z1 - MIN, z0 + delta)), z1]);
    else if (dragRef.current.handle === 'right') setZoomRange([z0, Math.min(1, Math.max(z0 + MIN, z1 + delta))]);
    else { const span = z1 - z0; const nz0 = Math.max(0, Math.min(1 - span, z0 + delta)); setZoomRange([nz0, nz0 + span]); }
  };
  const onNavMouseUp = () => { dragRef.current = null; };
  // Helper: pixel x within draw area → time in seconds
  const _twfXtoTime = (x: number, cW: number) => {
    const trace = Object.values(twfTraces).find(t => t != null);
    if (!trace) return null;
    const n = trace.y.length, sr = trace.sample_rate;
    const iStart = Math.floor(zoomRange[0] * n), iEnd = Math.ceil(zoomRange[1] * n);
    return iStart / sr + (Math.max(0, Math.min(cW, x)) / cW) * ((iEnd - iStart) / sr);
  };

  const onMainMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 48;
    const cW = rect.width - 56;
    if (x < 0 || x > cW) return;

    // Harmonic cursor hit-test
    if (twfHarmonicsEnabled && twfHarmonicCenter !== null) {
      const trace = Object.values(twfTraces).find(t => t != null);
      if (trace) {
        const n = trace.y.length, sr = trace.sample_rate;
        const iStart = Math.floor(zoomRange[0] * n), iEnd = Math.ceil(zoomRange[1] * n);
        const t0 = iStart / sr, dur = (iEnd - iStart) / sr;
        const startTimeSec = _twfXtoTime(x, cW)!;
        const HIT = 8;
        const toX = (t: number) => ((t - t0) / dur) * cW;
        if (Math.abs(x - toX(twfHarmonicCenter)) < HIT) {
          twfHarmonicDragRef.current = { mode: 'center', startTimeSec, startCenter: twfHarmonicCenter, startInterval: twfHarmonicInterval, sidebandN: 0 };
          return;
        }
        for (let sn = -5; sn <= 5; sn++) {
          if (sn === 0) continue;
          const sbT = twfHarmonicCenter + sn * twfHarmonicInterval;
          if (Math.abs(x - toX(sbT)) < HIT) {
            twfHarmonicDragRef.current = { mode: 'sideband', startTimeSec, startCenter: twfHarmonicCenter, startInterval: twfHarmonicInterval, sidebandN: sn };
            return;
          }
        }
      }
    }

    twfDragRef.current = { startPct: x / cW, startX: e.clientX };
    setTwfDragSelect(null);
  };

  const onMainMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 48;
    const cW = rect.width - 56;
    const pct = Math.max(0, Math.min(1, x / cW));
    setCursorPct(x >= 0 && x <= cW ? pct : null);

    if (twfHarmonicDragRef.current) {
      const currentT = _twfXtoTime(x, cW);
      if (currentT !== null) {
        const { mode, startTimeSec, startCenter, startInterval, sidebandN } = twfHarmonicDragRef.current;
        const delta = currentT - startTimeSec;
        if (mode === 'center') {
          setTwfHarmonicCenter(startCenter + delta);
        } else {
          const newSbT = (startCenter + sidebandN * startInterval) + delta;
          const newIvl = Math.abs(newSbT - startCenter) / Math.abs(sidebandN);
          const trace = Object.values(twfTraces).find(t => t != null);
          const minIvl = trace ? 2 / trace.sample_rate : 0.001;
          setTwfHarmonicInterval(Math.max(minIvl, newIvl));
        }
      }
      return;
    }

    if (twfDragRef.current && Math.abs(e.clientX - twfDragRef.current.startX) > 4) {
      setTwfDragSelect({ a: twfDragRef.current.startPct, b: pct });
    }
  };

  const onMainMouseUp = () => {
    if (twfHarmonicDragRef.current) { twfHarmonicDragRef.current = null; return; }
    if (twfDragRef.current && twfDragSelect) {
      const [z0, z1] = zoomRange;
      const zSpan = z1 - z0;
      const a = z0 + Math.min(twfDragSelect.a, twfDragSelect.b) * zSpan;
      const b = z0 + Math.max(twfDragSelect.a, twfDragSelect.b) * zSpan;
      if (b - a > 0.001) setZoomRange([a, b]);
      setTwfDragSelect(null);
    }
    twfDragRef.current = null;
  };
  const onMainWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const xPct = (e.clientX - rect.left - 48) / (rect.width - 56);
    const focus = zoomRange[0] + xPct * (zoomRange[1] - zoomRange[0]);
    const factor = e.deltaY > 0 ? 1.2 : 0.833;
    const span = Math.max(0.01, Math.min(1, (zoomRange[1] - zoomRange[0]) * factor));
    const z0 = Math.max(0, Math.min(1 - span, focus - xPct * span));
    setZoomRange([z0, z0 + span]);
  };

  // ── FFT mouse helpers
  const fftXtoCpm = (clientX: number, rect: DOMRect) => {
    const cW = rect.width - 58 - 16;
    const x = clientX - rect.left - 58;
    return xMinCpm + Math.max(0, Math.min(1, x / cW)) * (xMaxCpm - xMinCpm);
  };

  // ── FFT mouse handlers
  const onFftMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (rpmPickMode || placementMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - 58;
    const cW = rect.width - 58 - 16;
    if (x < 0 || x > cW) return;
    fftDragRef.current = { startCpm: fftXtoCpm(e.clientX, rect), startX: e.clientX };
    setFftDragSelect(null);
  };

  const onFftMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cpm = fftXtoCpm(e.clientX, rect);
    const x = e.clientX - rect.left - 58;
    const cW = rect.width - 58 - 16;
    if (x >= 0 && x <= cW) {
      setFftCursorCpm(cpm);
      setFftMousePos({ x: e.clientX, y: e.clientY });
    } else {
      setFftCursorCpm(null);
      setFftMousePos(null);
    }
    if (fftDragRef.current && Math.abs(e.clientX - fftDragRef.current.startX) > 4) {
      setFftDragSelect({ a: fftDragRef.current.startCpm, b: cpm });
    }
  };

  const onFftMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (fftDragRef.current && fftDragSelect) {
      const lo = Math.max(0, Math.min(fftDragSelect.a, fftDragSelect.b));
      const hi = Math.max(fftDragSelect.a, fftDragSelect.b);
      if (hi - lo > 100) { setXMinCpm(Math.round(lo)); setXMaxCpm(Math.round(hi)); }
      setFftDragSelect(null);
    }
    fftDragRef.current = null;
  };

  const onFftMouseLeave = () => {
    setFftCursorCpm(null);
    setFftMousePos(null);
    if (fftDragRef.current) { setFftDragSelect(null); fftDragRef.current = null; }
  };

  const onFftContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const cpm = Math.round(fftXtoCpm(e.clientX, rect));
    setContextMenu({ x: e.clientX, y: e.clientY, cpm });
    setMarkerForm(null);
  };

  const onTwfContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left - 48; // PAD.left = 48
    const cW = rect.width - 48 - 8;          // minus PAD.left + PAD.right
    const pct = Math.max(0, Math.min(1, relX / cW));
    const trace = Object.values(twfTraces).find(t => t != null);
    const sr = trace?.sample_rate ?? 1;
    const n = trace?.y.length ?? 0;
    const iStart = Math.floor(zoomRange[0] * n);
    const iEnd = Math.ceil(zoomRange[1] * n);
    const clickSec = Math.max(0.001, (iStart + pct * (iEnd - iStart)) / sr);
    setTwfContextMenu({ x: e.clientX, y: e.clientY, clickSec });
  };

  const handleLockZoom = () => {
    _waveformZoomLock = {
      fftXMin: xMinCpm, fftXMax: xMaxCpm, fftYMax: fftLastYMaxRef.current,
      twfZoomRange: zoomRange as [number, number],
      twfYMin: twfLastYRef.current.yMin, twfYMax: twfLastYRef.current.yMax,
    };
    setZoomLocked(true);
    setContextMenu(null);
    setTwfContextMenu(null);
  };

  const handleUnlockZoom = () => {
    _waveformZoomLock = null;
    setZoomLocked(false);
    setXMinCpm(0);
    setXMaxCpm(50000);
    setZoomRange([0, 1]);
    setContextMenu(null);
    setTwfContextMenu(null);
  };

  // Close context menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const fftEl = document.getElementById('fft-ctx-menu');
      if (fftEl && !fftEl.contains(e.target as Node)) setContextMenu(null);
      const twfEl = document.getElementById('twf-ctx-menu');
      if (twfEl && !twfEl.contains(e.target as Node)) setTwfContextMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── FFT click — RPM pick mode
  const onFftClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!rpmPickMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pad_left = 58, pad_right = 16;
    const cW = rect.width - pad_left - pad_right;
    const x = e.clientX - rect.left - pad_left;
    const cpm = Math.max(0, Math.round((x / cW) * xMaxCpm));
    const rpm = Math.round(cpm);
    setRpmConfirmPending({ cpm, rpm });
    setRpmPickMode(false);
  };

  // ── Copy to clipboard
  const handleCopy = async () => {
    const dateStr = currentGroup ? fmtEastern(currentGroup.measured_at) : '—';
    const pointStr = currentGroup?.point_name ?? '—';
    const sigStr = `${signalLabel(signal)} (${signalUnit(signal)})`;
    const lines: string[] = [];

    if (activeTab === 'twf') {
      lines.push(`TWF Analysis — ${pointStr} — ${dateStr}`);
      lines.push(`Signal: ${sigStr}`);
      lines.push('');
      for (const { label, t } of twfInfoItems) {
        lines.push(
          `Axis ${label}:  SR: ${(t.sample_rate ?? 0).toLocaleString()} Hz  |  Duration: ${t.duration_sec != null ? t.duration_sec.toFixed(3) : '—'}s  |  ` +
          `Peak+: ${t.peak_pos != null ? t.peak_pos.toFixed(4) : '—'}  |  Peak-: ${t.peak_neg != null ? t.peak_neg.toFixed(4) : '—'}  |  RMS: ${t.rms != null ? t.rms.toFixed(4) : '—'}  |  Crest: ${t.crest_factor != null ? t.crest_factor.toFixed(2) : '—'}  |  Source: ${t.source}`
        );
      }
      if (effectiveRpm) {
        lines.push('');
        lines.push(`RPM: ${effectiveRpm.toLocaleString()}${localRpm ? ' (local override)' : ''}`);
      }
    } else {
      lines.push(`FFT Analysis — ${pointStr} — ${dateStr}`);
      lines.push(`Signal: ${sigStr}`);
      const rpmStr = effectiveRpm
        ? `${effectiveRpm.toLocaleString()}${localRpm ? ' (local override)' : ''}`
        : detectedRpm ? `~${Math.round(detectedRpm).toLocaleString()} (auto-detected)` : '—';
      lines.push(`RPM: ${rpmStr}`);
      lines.push('');
      for (const [label, trace] of Object.entries(fftTraces)) {
        if (!trace || !activeAxes.has(label)) continue;
        const rms = (trace.amplitudes.reduce((s, v) => s + v * v, 0) / trace.amplitudes.length) ** 0.5;
        lines.push(
          `Axis ${label}:  Peak: ${Math.round(trace.peak_cpm).toLocaleString()} CPM (${trace.peak_amplitude.toFixed(5)})  |  ` +
          `RMS: ${rms.toFixed(5)}  |  SR: ${trace.sample_rate.toLocaleString()} Hz  |  Res: ${trace.freq_res_cpm.toFixed(1)} CPM  |  Src: ${trace.source}`
        );
      }
      if (fftCursorCpm !== null && fftCursorReadout?.length) {
        lines.push('');
        lines.push(`Cursor: ${Math.round(fftCursorCpm).toLocaleString()} CPM`);
        for (const r of fftCursorReadout) lines.push(`  ${r.label}: ${r.amp}`);
      }
    }

    await navigator.clipboard.writeText(lines.join('\n'));
    setCopyConfirmed(true);
    setTimeout(() => setCopyConfirmed(false), 2000);
  };

  const startPlacement = (type: MarkerType, cpm: number) => {
    setPlacementMode({ markerType: type, step: 'position', cursorCpm: cpm, spacing: 1000, count: 10 });
    setContextMenu(null);
  };

  // ── Cursor readout for FFT
  const fftCursorReadout = useMemo(() => {
    if (fftCursorCpm === null || fftDragSelect) return null;
    const result: { label: string; amp: string; colour: string }[] = [];
    for (const [label, trace] of Object.entries(fftTraces)) {
      if (!trace || !activeAxes.has(label)) continue;
      const binIdx = Math.min(Math.round(fftCursorCpm / trace.freq_res_cpm), trace.amplitudes.length - 1);
      result.push({ label, amp: trace.amplitudes[binIdx].toFixed(5), colour: AXIS_COLORS[label] || '#94a3b8' });
    }
    return result;
  }, [fftCursorCpm, fftDragSelect, fftTraces, activeAxes]);

  // ── Readout during placement mode cursor positioning (keyboard-driven, no mouse)
  const placementReadout = useMemo(() => {
    if (!placementMode || placementMode.step !== 'position') return null;
    const cpm = placementMode.cursorCpm;
    const result: { label: string; amp: string; colour: string }[] = [];
    for (const [label, trace] of Object.entries(fftTraces)) {
      if (!trace || !activeAxes.has(label)) continue;
      const binIdx = Math.min(Math.round(cpm / trace.freq_res_cpm), trace.amplitudes.length - 1);
      result.push({ label, amp: trace.amplitudes[binIdx].toFixed(5), colour: AXIS_COLORS[label] || '#94a3b8' });
    }
    return { cpm, axes: result };
  }, [placementMode, fftTraces, activeAxes]);

  // ── Readout during sideband position/spacing step — shows amplitudes at fund ± spacing
  const sidebandSpacingReadout = useMemo(() => {
    if (!placementMode || placementMode.markerType !== 'sideband' || (placementMode.step !== 'position' && placementMode.step !== 'spacing')) return null;
    const fund = placementMode.step === 'position' ? placementMode.cursorCpm : placementMode.fundamentalCpm!;
    const spacing = placementMode.spacing;
    const positions = [
      { label: '−1×', cpm: fund - spacing },
      { label: 'Fund', cpm: fund },
      { label: '+1×', cpm: fund + spacing },
    ];
    return positions.map(pos => {
      const axes: { label: string; amp: string; colour: string }[] = [];
      for (const [axLabel, trace] of Object.entries(fftTraces)) {
        if (!trace || !activeAxes.has(axLabel)) continue;
        const binIdx = Math.min(Math.round(Math.max(0, pos.cpm) / trace.freq_res_cpm), trace.amplitudes.length - 1);
        axes.push({ label: axLabel, amp: trace.amplitudes[binIdx].toFixed(5), colour: AXIS_COLORS[axLabel] || '#94a3b8' });
      }
      return { label: pos.label, cpm: pos.cpm, axes };
    });
  }, [placementMode, fftTraces, activeAxes]);

  const firstFftTrace = Object.values(fftTraces).find(t => t != null);
  const detectedRpm = firstFftTrace?.detected_rpm ?? null;

  // ── TWF info items
  const twfInfoItems = useMemo(() =>
    Object.entries(twfTraces).filter(([l, t]) => t && activeAxes.has(l)).map(([l, t]) => ({ label: l, colour: AXIS_COLORS[l] || '#94a3b8', t: t! })),
    [twfTraces, activeAxes]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render guards
  // ─────────────────────────────────────────────────────────────────────────
  if (groupsLoading) return (
    <div ref={containerRef} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading available waveforms…</div>
    </div>
  );
  if (groups.length === 0) return (
    <div ref={containerRef} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <h3 className="text-gray-900 mb-2">Waveform Analysis</h3>
      <p className="text-gray-400 text-sm">No waveform data available for this machine.</p>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Shared header controls
  // ─────────────────────────────────────────────────────────────────────────
  const sharedHeader = (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      {/* Title + nav */}
      <div className="flex items-center gap-3">
        <h3 className="text-gray-900 font-semibold text-sm">Waveform Analysis</h3>
          <button
            title="Open full-screen analysis"
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-600 transition-colors px-1.5 py-0.5 rounded hover:bg-gray-100"
            onClick={() => {
              const state = {
                machineId: machineId,
                operatingRpm: operatingRpm ?? null,
                isVfd: isVfd ?? false,
                selectedDate,
                selectedPoint,
                signal,
                activeAxes: Array.from(activeAxes),
                markers,
                xMaxCpm,
                currentGroup,
              };
              sessionStorage.setItem('pc_analysis_state', JSON.stringify(state));
              window.open('/analysis', '_blank');
            }}
          >
            <Maximize2 size={13} />
            <span>Expand</span>
          </button>
        <button
          title="Copy analysis to clipboard"
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-teal-600 transition-colors px-1.5 py-0.5 rounded hover:bg-gray-100"
        >
          {copyConfirmed ? <Check size={13} className="text-teal-600" /> : <Copy size={13} />}
          <span>{copyConfirmed ? 'Copied!' : 'Copy'}</span>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={goPrev} disabled={currentGroupIdx <= 0} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          </button>
          <span className="text-xs text-gray-500 px-1">{currentGroupIdx + 1} / {navGroups.length}</span>
          <button onClick={goNext} disabled={currentGroupIdx >= navGroups.length - 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
        {uniquePoints.length > 1 && (
          <select
            value={pointFilter}
            onChange={e => setPointFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white text-gray-600 focus:outline-none focus:border-teal-400 max-w-[130px]"
            title="Filter by point"
          >
            <option value="">All points</option>
            {uniquePoints.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
        <button onClick={() => setActiveTab('twf')} className={`px-3 py-1.5 ${activeTab === 'twf' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>TWF</button>
        <button onClick={() => setActiveTab('fft')} className={`px-3 py-1.5 border-l border-gray-200 ${activeTab === 'fft' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>FFT</button>
      </div>

      {/* Signal toggle — envelope disabled for live (MQTT) measurements; velocity is derived */}
      {(() => {
        const isMqtt = currentGroup?.axes.some(a => a.data_source === 'mqtt') ?? false;
        return (
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            {SIGNALS.map((s, i) => {
              const unavailable = isMqtt && s === 'envelope';
              return (
                <button key={s}
                  onClick={() => !unavailable && setSignal(s)}
                  title={unavailable ? 'Envelope not available for live sensor measurements' : undefined}
                  className={`px-3 py-1.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${
                    signal === s ? 'bg-blue-600 text-white' :
                    unavailable ? 'bg-white text-gray-300 cursor-not-allowed' :
                    'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >{signalLabel(s)}</button>
              );
            })}
          </div>
        );
      })()}
    </div>
  );

  // Date + point bar
  const dateBar = (
    <div className="flex items-center gap-3 mb-2 flex-wrap">
      <span className="text-sm font-medium text-gray-700">{currentGroup ? fmtEastern(currentGroup.measured_at) : '—'}</span>
      {pointsForDate.length > 1 && (
        <div className="flex gap-1 flex-wrap">
          {pointsForDate.map(g => (
            <button key={g.point_name} onClick={() => setSelectedPoint(g.point_name)}
              className={`text-xs px-2 py-0.5 rounded-full border ${g.point_name === selectedPoint ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            >{g.point_name}</button>
          ))}
        </div>
      )}
      {currentGroup && pointsForDate.length === 1 && <span className="text-xs text-gray-400">{currentGroup.point_name}</span>}
    </div>
  );

  // Axis legend toggles
  const axisLegend = (
    <div className="flex items-center gap-3 mb-2 flex-wrap">
      {Object.entries(AXIS_COLORS).map(([label, color]) => {
        const active = activeAxes.has(label);
        return (
          <button key={label} onClick={() => toggleAxis(label)}
            className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border transition-all ${active ? 'border-gray-200 text-gray-700 bg-white' : 'border-gray-100 text-gray-300 bg-gray-50'}`}
          >
            <span className="inline-block w-5 h-0.5 rounded" style={{ backgroundColor: active ? color : '#d1d5db' }} />
            <span>{label}</span>
          </button>
        );
      })}
      <div className="ml-auto flex items-center gap-2">
        {activeTab === 'fft' && (
          <button
            onClick={() => { setRpmPickMode(v => !v); setRpmConfirmPending(null); }}
            title="Click a frequency on the FFT to set RPM for this waveform"
            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all ${rpmPickMode ? 'bg-amber-500 border-amber-400 text-white' : 'border-gray-200 text-gray-500 bg-white hover:bg-gray-50'}`}
          >
            <span className="font-mono">RPM</span>
            <span>{localRpm ? `${localRpm.toLocaleString()} ✎` : 'Set'}</span>
          </button>
        )}
        {effectiveRpm && effectiveRpm > 0 && (
          <button onClick={() => setShowRevMarkers(v => !v)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${showRevMarkers ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-gray-200 text-gray-500 bg-white hover:bg-gray-50'}`}
          >
            <span className="font-mono">1×</span><span>{activeTab === 'fft' ? 'Harmonics' : 'Rev markers'}</span>
          </button>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // FFT tab content
  // ─────────────────────────────────────────────────────────────────────────
  const fftContent = (
    <div className="flex gap-4">
      {/* Chart column */}
      <div className="flex-1 min-w-0">
        {rpmPickMode && (
          <div className="mb-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-2">
            <span className="text-xs text-amber-700">
              <span className="font-semibold">Click on the FFT</span> at the frequency that represents 1× RPM — harmonics will update automatically
            </span>
            <button onClick={() => setRpmPickMode(false)} className="text-amber-400 hover:text-amber-600 text-xs shrink-0">✕</button>
          </div>
        )}
        {placementMode && (
          <div className="mb-1.5 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <span className="text-xs text-indigo-700">
                {placementMode.step === 'position' && (
                  placementMode.markerType === 'harmonic'
                    ? <><span className="font-semibold">Harmonic</span> — move cursor to set 1× frequency · harmonics preview shown · <kbd className="bg-white border border-indigo-200 rounded px-1">← →</kbd> (Shift=50 bins) · <kbd className="bg-white border border-indigo-200 rounded px-1">Enter</kbd> to confirm · <kbd className="bg-white border border-indigo-200 rounded px-1">Esc</kbd> cancel</>
                    : <><span className="font-semibold capitalize">{placementMode.markerType}</span> — move cursor with <kbd className="bg-white border border-indigo-200 rounded px-1">← →</kbd> (Shift=50 bins) · <kbd className="bg-white border border-indigo-200 rounded px-1">Enter</kbd> to confirm · <kbd className="bg-white border border-indigo-200 rounded px-1">Esc</kbd> cancel</>
                )}
                {placementMode.step === 'spacing' && (
                  <><span className="font-semibold">Sideband spacing:</span> <span className="font-mono font-semibold">{Math.round(placementMode.spacing).toLocaleString()} CPM</span> <span className="text-indigo-400">({(placementMode.spacing / 60).toFixed(1)} Hz)</span> — <kbd className="bg-white border border-indigo-200 rounded px-1">← →</kbd> adjust · <kbd className="bg-white border border-indigo-200 rounded px-1">Enter</kbd> to confirm</>
                )}
                {placementMode.step === 'count' && (
                  <>Count: <span className="font-mono font-semibold">{placementMode.count}</span> {placementMode.markerType === 'harmonic' ? 'harmonics' : 'sidebands each side'} — <kbd className="bg-white border border-indigo-200 rounded px-1">← →</kbd> adjust · <kbd className="bg-white border border-indigo-200 rounded px-1">Enter</kbd> to save</>
                )}
              </span>
              {/* Live frequency + amplitude readout during positioning */}
              {placementReadout && placementReadout.axes.length > 0 && (
                <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-mono font-semibold text-indigo-800">
                    {Math.round(placementReadout.cpm).toLocaleString()} CPM
                    <span className="text-indigo-400 font-normal ml-1">({(placementReadout.cpm / 60).toFixed(1)} Hz)</span>
                  </span>
                  {placementReadout.axes.map(r => (
                    <span key={r.label} className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.colour }} />
                      <span className="text-indigo-600">{r.label}:</span>
                      <span className="font-mono text-indigo-900 font-medium">{r.amp}</span>
                    </span>
                  ))}
                </div>
              )}
              {/* Sideband spacing step — amplitude table at fund ± spacing */}
              {sidebandSpacingReadout && sidebandSpacingReadout.length > 0 && (
                <div className="mt-2 grid gap-y-1" style={{ gridTemplateColumns: 'auto 1fr' }}>
                  {sidebandSpacingReadout.map(row => (
                    <div key={row.label} className="contents">
                      <span className="text-xs font-mono text-indigo-500 pr-3 whitespace-nowrap">
                        {row.label} <span className="text-indigo-400">{Math.round(row.cpm).toLocaleString()} CPM</span>
                      </span>
                      <span className="flex items-center gap-3 flex-wrap">
                        {row.axes.map(r => (
                          <span key={r.label} className="text-xs flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.colour }} />
                            <span className="text-indigo-600">{r.label}:</span>
                            <span className="font-mono text-indigo-900 font-medium">{r.amp}</span>
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setPlacementMode(null)} className="text-indigo-400 hover:text-indigo-600 text-xs shrink-0 mt-0.5">✕</button>
          </div>
        )}
        <div className="relative rounded-lg overflow-hidden border border-gray-100" style={{ background: '#fff' }}>
          {Object.keys(fftLoading).length > 0 && Object.keys(fftTraces).length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/80">
              <span className="text-gray-400 text-sm">Loading FFT…</span>
            </div>
          )}
          {fftError && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/80">
              <span className="text-red-500 text-sm">{fftError}</span>
            </div>
          )}
          <canvas
            ref={fftCanvasRef}
            style={{ width: '100%', height: 280, display: 'block', cursor: fftDragSelect ? 'col-resize' : 'crosshair' }}
            onMouseDown={onFftMouseDown}
            onMouseMove={onFftMouseMove}
            onMouseUp={onFftMouseUp}
            onMouseLeave={onFftMouseLeave}
            onContextMenu={onFftContextMenu}
            onClick={onFftClick}
          />
          {/* Floating hover tooltip */}
          {fftMousePos && fftCursorReadout && fftCursorReadout.length > 0 && (
            <div
              className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs pointer-events-none"
              style={{ left: fftMousePos.x + 14, top: fftMousePos.y - 12 }}
            >
              <div className="font-semibold text-gray-700 mb-1">{Math.round(fftCursorCpm!).toLocaleString()} CPM</div>
              {fftCursorReadout.map(r => (
                <div key={r.label} className="flex items-center gap-2 py-0.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.colour }} />
                  <span className="text-gray-500 w-3">{r.label}</span>
                  <span className="font-mono text-gray-700">{r.amp}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* X range slider */}
        <div className="mt-2 px-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-gray-500 w-36 shrink-0">
              {xMinCpm > 0 ? `${xMinCpm.toLocaleString()} – ` : '0 – '}{xMaxCpm.toLocaleString()} CPM
            </span>
            <input type="range" min={1000} max={300000} step={1000} value={xMaxCpm}
              onChange={e => { setXMaxCpm(Number(e.target.value)); setXMinCpm(0); }}
              className="flex-1 h-1.5 rounded accent-teal-600"
            />
            {xMinCpm > 0 && (
              <button onClick={() => { setXMinCpm(0); }}
                className="text-xs px-2 py-0.5 rounded border border-indigo-200 text-indigo-500 hover:bg-indigo-50 shrink-0">
                Reset zoom
              </button>
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            {[10000, 25000, 50000, 100000, 200000, 300000].map(v => (
              <button key={v} onClick={() => { setXMaxCpm(v); setXMinCpm(0); }}
                className={`text-xs px-2 py-0.5 rounded border ${xMaxCpm === v && xMinCpm === 0 ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >{v >= 1000 ? `${v / 1000}k` : v}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Info panel */}
      <div className="w-44 shrink-0 text-xs space-y-3">
        {/* Axis toggles + loading state */}
        <div className="space-y-1">
          {['H', 'V', 'A'].map(label => {
            const colour = AXIS_COLORS[label];
            const trace = fftTraces[label];
            const loading = fftLoading[label];
            const active = activeAxes.has(label);
            const rms = trace ? (trace.amplitudes.reduce((s, v) => s + v * v, 0) / trace.amplitudes.length) ** 0.5 : null;
            return (
              <div key={label} className="flex items-center gap-2">
                <button onClick={() => toggleAxis(label)} className="flex items-center gap-1.5 flex-1">
                  <span className="w-2.5 h-2.5 rounded-full border-2 flex-shrink-0" style={{ backgroundColor: active ? colour : 'transparent', borderColor: colour }} />
                  <span className={active ? 'text-gray-700 font-medium' : 'text-gray-400'}>{label}</span>
                </button>
                <span className="text-gray-400 font-mono">
                  {loading ? <span className="animate-pulse">…</span> : trace ? rms!.toFixed(4) : '—'}
                </span>
              </div>
            );
          })}
        </div>

        <div className="border-t border-gray-100 pt-2 space-y-1">
          <div className="text-gray-500 flex items-center gap-1">
            <span>RPM:</span>
            <span className={`font-medium ${localRpm ? 'text-amber-600' : 'text-gray-800'}`}>
              {effectiveRpm ? effectiveRpm.toLocaleString() : detectedRpm ? `~${Math.round(detectedRpm).toLocaleString()}` : '—'}
            </span>
            {localRpm && (
              <button onClick={() => setLocalRpm(null)} title="Clear local RPM override" className="text-gray-300 hover:text-red-400 leading-none text-xs ml-auto">✕</button>
            )}
          </div>
          {firstFftTrace && <>
            <div className="text-gray-500">Res: <span className="font-mono text-gray-700">{firstFftTrace.freq_res_cpm.toFixed(1)} CPM</span></div>
            <div className="text-gray-500">SR: <span className="font-mono text-gray-700">{firstFftTrace.sample_rate.toLocaleString()} Hz</span></div>
            <div className="text-gray-500">Bins: <span className="font-mono text-gray-700">{firstFftTrace.bin_count.toLocaleString()}</span></div>
            <div className="text-gray-500">Src: <span className={firstFftTrace.source === 'spaces' ? 'text-teal-600 font-medium' : 'text-gray-700'}>{firstFftTrace.source === 'spaces' ? 'Spaces ✓' : 'EI-Analytic'}</span></div>
          </>}
        </div>

        {/* PeakVue info — always visible, property of the waveform not the signal */}
        {Object.keys(pvDataByAxis).length > 0 && (
          <div className="border-t border-gray-100 pt-2">
            <div className="text-gray-500 font-medium mb-1.5">Impact View {pvLoading && <span className="text-gray-300 animate-pulse">…</span>}</div>
            {(['H', 'V', 'A'] as const).filter(l => pvDataByAxis[l] && activeAxes.has(l)).map(label => {
              const pv   = pvDataByAxis[label];
              const sc   = PV_SEV_COLOURS[pv.severity] ?? '#6b7280';
              const plus = pvPlusFromData(pv);
              return (
                <div key={label} className="mb-2">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: AXIS_COLORS[label] }} />
                    <span className="text-gray-600 font-medium">{label}</span>
                    <span className="ml-auto px-1.5 py-0.5 rounded text-xs font-semibold capitalize" style={{ backgroundColor: sc + '22', color: sc }}>
                      {pv.severity}
                    </span>
                  </div>
                  <div className="pl-3.5 text-gray-500 space-y-0.5">
                    <div>MaxPk <span className="font-mono text-gray-700">{pv.max_peak_g.toFixed(3)} G</span></div>
                    <div>RMS <span className="font-mono text-gray-700">{pv.rms_g.toFixed(3)} G</span></div>
                    <div>CF <span className="font-mono text-gray-700">{pv.crest_factor.toFixed(2)}</span></div>
                  </div>
                  {plus && plus.plus_reliability !== 'inactive' && (
                    <div className="pl-3.5 mt-1.5 border-t border-gray-100 pt-1.5">
                      <div className="flex items-center gap-1 mb-1">
                        <span className="text-gray-500 text-xs font-medium">Plus</span>
                        {plus.plus_reliability === 'preliminary'
                          ? <span className="text-amber-500 text-xs">⚠ Prelim.</span>
                          : <span className="text-teal-600 text-xs">● High conf.</span>}
                      </div>
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-gray-400 text-xs w-12">Periodic</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full" style={{ width: `${plus.periodic_pct}%`, backgroundColor: periodicBarColour(plus.periodic_pct, plus.fault_type) }} />
                        </div>
                        <span className="text-gray-700 font-mono text-xs">{plus.periodic_pct.toFixed(0)}%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PV_PLUS_FAULT_COLOURS[plus.fault_type] ?? '#6b7280' }} />
                        <span className="text-xs" style={{ color: PV_PLUS_FAULT_COLOURS[plus.fault_type] ?? '#6b7280' }}>
                          {PV_PLUS_FAULT_LABELS[plus.fault_type] ?? plus.fault_type}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Cursor readout */}
        {fftCursorCpm !== null && (
          <div className="border-t border-gray-100 pt-2 space-y-0.5">
            <div className="text-gray-600 font-medium">{Math.round(fftCursorCpm).toLocaleString()} CPM</div>
            {fftCursorReadout?.map(r => (
              <div key={r.label} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: r.colour }} />
                <span className="text-gray-700 font-mono">{r.amp}</span>
              </div>
            ))}
          </div>
        )}

        {/* Marker management — always visible */}
        <div className="border-t border-gray-100 pt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 font-medium">Markers {markers.length > 0 && <span className="text-gray-400 font-normal">({markers.length})</span>}</span>
            {markers.length > 0 && (
              <div className="flex gap-1">
                <button onClick={() => setMarkers(prev => prev.slice(0, -1))} title="Remove last marker" className="text-xs text-gray-400 hover:text-orange-500 px-1">↩</button>
                <button onClick={() => setMarkers([])} title="Clear all markers" className="text-xs text-gray-400 hover:text-red-500 px-1">✕ all</button>
              </div>
            )}
          </div>
          {markers.length === 0 ? (
            <div className="text-gray-300 text-xs">Right-click FFT to add</div>
          ) : (
            markers.map(m => (
              <div key={m.id} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: m.colour }} />
                <span className="text-gray-600 flex-1 truncate">{m.label}</span>
                <button onClick={() => setMarkers(prev => prev.filter(x => x.id !== m.id))} className="text-gray-300 hover:text-red-400 text-xs leading-none">×</button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TWF tab content
  // ─────────────────────────────────────────────────────────────────────────
  const twfContent = (
    <>
      {twfPlacementMode && (
        <div className="mb-1.5 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <span className="text-xs text-indigo-700">
              {twfPlacementMode.step === 'position' && (
                <><span className="font-semibold">Period markers</span> — move cursor to set period · harmonics shown as preview · <kbd className="bg-white border border-indigo-200 rounded px-1">← →</kbd> (Shift=50 samples) · <kbd className="bg-white border border-indigo-200 rounded px-1">Enter</kbd> to confirm · <kbd className="bg-white border border-indigo-200 rounded px-1">Esc</kbd> cancel</>
              )}
              {twfPlacementMode.step === 'count' && (
                <>Count: <span className="font-mono font-semibold">{twfPlacementMode.count}</span> periods — <kbd className="bg-white border border-indigo-200 rounded px-1">← →</kbd> adjust · <kbd className="bg-white border border-indigo-200 rounded px-1">Enter</kbd> to save</>
              )}
            </span>
            {twfPlacementMode.step === 'position' && (
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <span className="text-xs font-mono font-semibold text-indigo-800">
                  T = {twfPlacementMode.cursorSec.toFixed(4)}s
                  <span className="text-indigo-400 font-normal ml-2">{(1 / twfPlacementMode.cursorSec).toFixed(1)} Hz · {Math.round(60 / twfPlacementMode.cursorSec).toLocaleString()} CPM</span>
                </span>
              </div>
            )}
          </div>
          <button onClick={() => setTwfPlacementMode(null)} className="text-indigo-400 hover:text-indigo-600 text-xs shrink-0 mt-0.5">✕</button>
        </div>
      )}
      {(zoomRange[0] > 0.001 || zoomRange[1] < 0.999) && (
        <div className="flex justify-end mb-1">
          <button onClick={() => setZoomRange([0, 1])} className="text-xs px-2 py-0.5 border border-gray-200 rounded text-gray-500 hover:bg-gray-50">Reset Zoom</button>
        </div>
      )}
      <div className="relative rounded-lg overflow-hidden border border-gray-100">
        {twfLoading && <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/80"><span className="text-gray-400 text-sm">Loading waveform…</span></div>}
        {twfError && <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/80"><span className="text-red-400 text-sm">Error: {twfError}</span></div>}
        <canvas ref={mainCanvasRef} style={{ width: '100%', height: 240, display: 'block', cursor: twfHarmonicDragRef.current ? 'grabbing' : twfDragSelect ? 'col-resize' : 'crosshair' }}
          onMouseDown={onMainMouseDown} onMouseMove={onMainMouseMove}
          onMouseUp={onMainMouseUp} onMouseLeave={() => { setCursorPct(null); onMainMouseUp(); }}
          onWheel={onMainWheel} onContextMenu={onTwfContextMenu} />
      </div>
      {twfHarmonicReadout && (
        <div className="mt-1 px-2 py-1.5 bg-red-50 border border-red-100 rounded text-xs font-mono text-red-700 flex flex-wrap items-center gap-x-4 gap-y-0.5">
          <span>Fund left: <span className="font-semibold">(Amp: {twfHarmonicReadout.lAmp.toFixed(4)}, Time: {twfHarmonicReadout.lT.toFixed(4)}s)</span></span>
          <span>Fund right: <span className="font-semibold">(Amp: {twfHarmonicReadout.rAmp.toFixed(4)}, Time: {twfHarmonicReadout.rT.toFixed(4)}s)</span></span>
          <span>Interval: <span className="font-semibold">{twfHarmonicInterval.toFixed(5)}s</span></span>
          <span>Freq: <span className="font-semibold">{twfHarmonicReadout.cpm.toLocaleString()} CPM</span></span>
          <span className="text-red-400 text-[10px] ml-auto">drag lines · ←→ interval · Ctrl+←→ center · Shift=×10</span>
        </div>
      )}
      <div className="mt-1 rounded overflow-hidden border border-gray-100">
        <canvas ref={navCanvasRef} style={{ width: '100%', height: 48, display: 'block', cursor: 'ew-resize' }}
          onMouseDown={onNavMouseDown} onMouseMove={onNavMouseMove} onMouseUp={onNavMouseUp} onMouseLeave={onNavMouseUp} />
      </div>
      <p className="text-xs text-gray-400 mt-1">Drag handles to zoom · Scroll on chart to zoom · Drag middle to pan</p>

      {/* RPM info row */}
      {effectiveRpm && effectiveRpm > 0 && twfInfoItems.length > 0 && (() => {
        const revPeriodSec = 60 / effectiveRpm;
        const durSec = twfInfoItems[0]?.t.duration_sec ?? 0;
        const revsInRec = durSec > 0 ? Math.floor(durSec / revPeriodSec) : 0;
        return (
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-500 px-1">
            <span>RPM: <span className={`font-mono ${localRpm ? 'text-amber-600 font-semibold' : 'text-gray-700'}`}>{isVfd ? '~' : ''}{effectiveRpm.toLocaleString()}{isVfd ? ' (VFD)' : ''}{localRpm ? ' (override)' : ''}</span></span>
            <span>1 rev: <span className="text-gray-700 font-mono">{revPeriodSec.toFixed(4)}s</span></span>
            {revsInRec > 0 && <span>Revs in recording: <span className="text-gray-700 font-mono">{revsInRec}</span></span>}
          </div>
        );
      })()}

      {/* Info cards */}
      {twfInfoItems.length > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {twfInfoItems.map(({ label, colour, t }) => (
            <div key={label} className="rounded-lg border border-gray-100 bg-white p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colour }} />
                <span className="text-xs font-semibold text-gray-700">Axis {label}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {[['SR', (t.sample_rate ?? 0).toLocaleString() + ' Hz'], ['Duration', t.duration_sec != null ? t.duration_sec.toFixed(3) + 's' : '—'],
                  ['Peak+', t.peak_pos != null ? t.peak_pos.toFixed(4) : '—'], ['Peak−', t.peak_neg != null ? t.peak_neg.toFixed(4) : '—'],
                  ['RMS', t.rms != null ? t.rms.toFixed(4) : '—'], ['Crest', t.crest_factor != null ? t.crest_factor.toFixed(2) : '—'],
                  ['Source', t.source === 'spaces' ? 'Spaces' : 'API']].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-xs">
                    <span className="text-gray-400">{k}</span>
                    <span className="text-gray-700 font-mono">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PeakVue cards — shown below signal info, visible regardless of selected signal */}
      {Object.keys(pvDataByAxis).length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-500">Impact View</span>
            <span className="text-xs text-gray-300">1000 Hz HP · stress wave amplitude</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(['H', 'V', 'A'] as const).filter(l => pvDataByAxis[l]).map(label => {
              const pv   = pvDataByAxis[label];
              const sc   = PV_SEV_COLOURS[pv.severity] ?? '#6b7280';
              const plus = pvPlusFromData(pv);
              const rel  = plus?.plus_reliability ?? 'inactive';
              return (
                <div key={label}
                  className="rounded-lg border bg-white p-3 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderColor: sc + '60' }}
                  onClick={() => {
                    setSignal('peakvue');
                    setActiveAxes(prev => {
                      const next = new Set(prev);
                      next.add(label);
                      return next;
                    });
                  }}
                  title={`Click to view ${label} axis Impact View waveform`}
                >
                  {/* Base PeakVue header */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: AXIS_COLORS[label] }} />
                    <span className="text-xs font-semibold text-gray-700">Axis {label}</span>
                    <span className="ml-auto px-1.5 py-0.5 rounded text-xs font-semibold capitalize" style={{ backgroundColor: sc + '22', color: sc }}>
                      {pv.severity}
                    </span>
                  </div>
                  {selectedPoint && (
                    <div className="text-xs text-gray-400 mb-2 pl-4 truncate">{selectedPoint}</div>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2">
                    {[['MaxPk', pv.max_peak_g.toFixed(3) + ' G'], ['RMS', pv.rms_g.toFixed(3) + ' G'],
                      ['CF', pv.crest_factor.toFixed(2)], ['HP cut', '1,000 Hz'],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-gray-400">{k}</span>
                        <span className="text-gray-700 font-mono">{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* PeakVue Plus section */}
                  {plus && rel !== 'inactive' && (
                    <div className="border-t border-gray-100 pt-2 mt-1">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-xs font-semibold text-gray-500">Impact View Plus</span>
                        {rel === 'preliminary'
                          ? <span className="text-amber-500 text-xs">⚠ Preliminary</span>
                          : <span className="text-teal-600 text-xs font-medium">● High confidence</span>}
                      </div>

                      {/* Severity bar (high only) */}
                      {rel === 'high' && (
                        <div className="mb-2">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-gray-400 text-xs">Severity</span>
                            <span className="text-gray-700 font-mono text-xs">{pv.max_peak_g.toFixed(1)} G</span>
                          </div>
                          <div className="relative bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{
                              width: `${Math.min(100, (pv.max_peak_g / 40) * 100)}%`,
                              backgroundColor: sc,
                            }} />
                            {[5, 10, 20, 40].map(tick => (
                              <div key={tick} className="absolute top-0 h-2 w-px bg-white/60" style={{ left: `${(tick / 40) * 100}%` }} />
                            ))}
                          </div>
                          <div className="flex justify-between mt-0.5">
                            {['5', '10', '20', '40G'].map(t => (
                              <span key={t} className="text-gray-300 text-xs">{t}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Periodic bar */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-gray-400 text-xs">Periodic</span>
                          <span className="text-gray-700 font-mono text-xs">{plus.periodic_pct.toFixed(0)}%</span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-2">
                          <div className="h-2 rounded-full transition-all" style={{
                            width: `${plus.periodic_pct}%`,
                            backgroundColor: periodicBarColour(plus.periodic_pct, plus.fault_type),
                          }} />
                        </div>
                      </div>

                      {/* Fault type badge */}
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: PV_PLUS_FAULT_COLOURS[plus.fault_type] ?? '#6b7280' }} />
                        <span className="text-xs font-semibold"
                          style={{ color: PV_PLUS_FAULT_COLOURS[plus.fault_type] ?? '#6b7280' }}>
                          {PV_PLUS_FAULT_LABELS[plus.fault_type] ?? plus.fault_type}
                        </span>
                      </div>

                      {/* Interpretation text */}
                      {plus.interpretation && (
                        <p className="text-xs text-gray-400 leading-relaxed">{plus.interpretation}</p>
                      )}

                      {rel === 'preliminary' && (
                        <p className="text-xs text-amber-500 mt-1">Low amplitude — increase monitoring frequency.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Context menu
  // ─────────────────────────────────────────────────────────────────────────
  const ctxMenu = contextMenu && (
    <div id="fft-ctx-menu" className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm min-w-[200px]"
      style={{ left: contextMenu.x, top: contextMenu.y }}>
      <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100 font-mono">{Math.round(contextMenu.cpm).toLocaleString()} CPM</div>
      <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => startPlacement('standalone', contextMenu.cpm)}>Add standalone marker</button>
      <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => startPlacement('harmonic', contextMenu.cpm)}>Add harmonic series</button>
      <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => startPlacement('sideband', contextMenu.cpm)}>Add sideband markers</button>
      <div className="border-t border-gray-100">
        <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-red-500" onClick={() => { setMarkers([]); setContextMenu(null); }}>Clear all markers</button>
      </div>
      <div className="border-t border-gray-100">
        {zoomLocked
          ? <button className="w-full text-left px-3 py-2 hover:bg-orange-50 text-orange-600 font-medium" onClick={handleUnlockZoom}>🔓 Unlock zoom</button>
          : <button className="w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-600 font-medium" onClick={handleLockZoom}>🔒 Lock zoom</button>
        }
      </div>
    </div>
  );

  const twfCtxMenu = twfContextMenu && (
    <div id="twf-ctx-menu" className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm min-w-[200px]"
      style={{ left: twfContextMenu.x, top: twfContextMenu.y }}>
      <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100">Waveform view</div>
      <button
        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700"
        onClick={() => {
          if (twfContextMenu?.clickSec) {
            setTwfPlacementMode({ step: 'position', cursorSec: twfContextMenu.clickSec, count: 10 });
          }
          setTwfContextMenu(null);
        }}
      >Add period markers</button>
      {twfHarmonicsEnabled ? (
        <button
          className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600"
          onClick={() => { setTwfHarmonicsEnabled(false); setTwfHarmonicCenter(null); setTwfContextMenu(null); }}
        >Clear harmonics</button>
      ) : (
        <button
          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700"
          onClick={() => {
            setTwfHarmonicsEnabled(true);
            if (twfContextMenu?.clickSec) setTwfHarmonicCenter(twfContextMenu.clickSec);
            setTwfContextMenu(null);
          }}
        >Add harmonics</button>
      )}
      <div className="border-t border-gray-100">
        {zoomLocked
          ? <button className="w-full text-left px-3 py-2 hover:bg-orange-50 text-orange-600 font-medium" onClick={handleUnlockZoom}>🔓 Unlock zoom</button>
          : <button className="w-full text-left px-3 py-2 hover:bg-blue-50 text-blue-600 font-medium" onClick={handleLockZoom}>🔒 Lock zoom</button>
        }
      </div>
      {twfMarkers.length > 0 && (
        <div className="border-t border-gray-100">
          <button className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600" onClick={() => { setTwfMarkers([]); setTwfContextMenu(null); }}>
            Clear period markers
          </button>
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Final render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
      {sharedHeader}
      {dateBar}
      {axisLegend}
      {zoomLocked && (
        <div className="flex items-center gap-1.5 mb-2 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 w-fit">
          <span>🔒</span>
          <span className="font-medium">Zoom locked</span>
          <span className="text-blue-400 mx-0.5">·</span>
          <span className="font-mono">{xMinCpm.toLocaleString()}–{xMaxCpm.toLocaleString()} CPM</span>
          <button onClick={handleUnlockZoom} className="ml-1 text-blue-400 hover:text-blue-600 underline">unlock</button>
        </div>
      )}
      {activeTab === 'twf' ? twfContent : fftContent}
      {ctxMenu}
      {twfCtxMenu}

      {/* RPM confirm dialog */}
      {rpmConfirmPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setRpmConfirmPending(null)}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h4 className="text-gray-900 font-semibold mb-1">Set operating RPM?</h4>
            <p className="text-sm text-gray-500 mb-1">
              Selected frequency: <span className="font-mono text-gray-700">{Math.round(rpmConfirmPending.cpm).toLocaleString()} CPM</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              This equals <span className="font-semibold text-amber-600 text-base">{rpmConfirmPending.rpm.toLocaleString()} RPM</span>.
              Harmonic markers and rev lines will update for this session.
            </p>
            {localRpm && (
              <p className="text-xs text-gray-400 mb-3">Current override: {localRpm.toLocaleString()} RPM</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setRpmConfirmPending(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >Cancel</button>
              <button
                onClick={() => {
                  setLocalRpm(rpmConfirmPending.rpm);
                  setShowRevMarkers(true);
                  setRpmConfirmPending(null);
                }}
                className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
              >Set {rpmConfirmPending.rpm.toLocaleString()} RPM</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
